import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message as antdMessage, Modal } from 'antd';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bot,
  BookOpen,
  Check,
  ChevronDown,
  Circle,
  Coins,
  Copy,
  History,
  Loader2,
  LogIn,
  Moon,
  Plus,
  Play,
  RefreshCw,
  Send,
  Settings,
  Sun,
  UserCircle,
  Wand2,
  X,
  Zap,
} from 'lucide-react';
import './WorkbenchPage.css';
import StagePanels from './WorkbenchStagePanels';
import UserAccountMenu from '../components/UserAccountMenu';
import AppendChaptersButton from '../components/AppendChaptersButton';
import { antiBrushHeaders, trackEvent } from '../utils/analytics';
import { parseServerDateTime } from '../utils/dateTime';
import { formatInkBalance } from '../utils/ink';
import { normalizeBackendChapterId } from '../utils/chapterId';
import {
  Chapter,
  TopTabKey,
  topTabs,
} from '../data/workbenchData';

const tabKeys = new Set<TopTabKey>(['inspiration', 'settings', 'characters', 'outline', 'chapters', 'review', 'publish']);
const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;

type AutoGenerateState = 'checking' | 'ready' | 'running' | 'done' | 'stopped';
type AutoGenerateStepStatus = 'pending' | 'running' | 'reviewing' | 'done' | 'failed' | 'stopped';
interface AutoGenerateStep {
  id: string;
  index: string;
  title: string;
  status: AutoGenerateStepStatus;
  message: string;
}
type AiMessage = { role: 'user' | 'assistant'; text: string };
type StageCompletion = Record<TopTabKey, boolean>;
type MarkdownBlock =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'heading'; text: string }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'unordered-list'; items: string[] };

const emptyChapter: Chapter = {
  id: 'chapter-empty',
  index: '01',
  title: '等待生成章节',
  progress: 0,
  status: 'todo',
  summary: '',
  words: 0,
  targetWords: 1000,
  content: '',
};

const emptyProjectMeta = {
  title: '未命名新书',
  fileName: '未命名新书.lumiu.md',
  platform: '通用',
  genre: '待选择',
};

const renderInlineMarkdown = (text: string) => {
  const nodes: React.ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(<strong key={`bold-${match.index}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : text;
};

const parseMarkdownBlocks = (text: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let orderedItems: string[] = [];
  let unorderedItems: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', lines: paragraph });
      paragraph = [];
    }
  };
  const flushLists = () => {
    if (orderedItems.length) {
      blocks.push({ type: 'ordered-list', items: orderedItems });
      orderedItems = [];
    }
    if (unorderedItems.length) {
      blocks.push({ type: 'unordered-list', items: unorderedItems });
      unorderedItems = [];
    }
  };

  text.replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushLists();
      return;
    }

    const headingMatch = trimmed.match(/^#{1,3}\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushLists();
      blocks.push({ type: 'heading', text: headingMatch[1].trim() });
      return;
    }

    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      if (unorderedItems.length) {
        blocks.push({ type: 'unordered-list', items: unorderedItems });
        unorderedItems = [];
      }
      orderedItems.push(orderedMatch[1].trim());
      return;
    }

    const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (orderedItems.length) {
        blocks.push({ type: 'ordered-list', items: orderedItems });
        orderedItems = [];
      }
      unorderedItems.push(unorderedMatch[1].trim());
      return;
    }

    flushLists();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushLists();
  return blocks;
};

const renderAssistantMarkdown = (text: string) => {
  const blocks = parseMarkdownBlocks(text);
  return (
    <div className="ai-markdown">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return <h4 key={`heading-${index}`}>{renderInlineMarkdown(block.text)}</h4>;
        }
        if (block.type === 'ordered-list') {
          return (
            <ol key={`ol-${index}`}>
              {block.items.map((item, itemIndex) => <li key={`${index}-${itemIndex}`}>{renderInlineMarkdown(item)}</li>)}
            </ol>
          );
        }
        if (block.type === 'unordered-list') {
          return (
            <ul key={`ul-${index}`}>
              {block.items.map((item, itemIndex) => <li key={`${index}-${itemIndex}`}>{renderInlineMarkdown(item)}</li>)}
            </ul>
          );
        }
        return (
          <p key={`p-${index}`}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`${index}-${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {renderInlineMarkdown(line)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

const stageScopeLabels: Record<TopTabKey, string> = {
  inspiration: '当前灵感梗概',
  settings: '当前设定',
  characters: '当前人物设定',
  outline: '当前大纲',
  chapters: '当前章节正文',
  review: '当前审稿结果',
  publish: '当前发布资料',
};

interface AiSession {
  id: string;
  title: string;
  last: string;
  messages: AiMessage[];
}

interface AiPanelLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface BackendProject {
  id: number;
  title: string;
  file_name: string;
  platform: string;
  genre: string;
  chapter_target: number;
  words_per_chapter: number;
  active_chapter_no?: number;
  publish?: Record<string, unknown>;
  settings?: {
    narrative_perspective?: string;
    perspective?: string;
    _workflow?: {
      source_inspiration_id?: number;
    };
  };
}

interface ReviewDimension {
  name?: string;
  score?: number;
  passed?: boolean;
  status?: string;
  comment?: string;
  suggestion?: string;
}

interface ReviewProblem {
  level?: string;
  message?: string;
  location?: string;
  suggestion?: string;
  required?: boolean;
}

interface ChapterReviewData {
  [key: string]: unknown;
  score?: number;
  dimensions?: ReviewDimension[];
  problems?: ReviewProblem[];
  suggestions?: string[];
  risk_flags?: string[];
  continuity_notes?: string[];
}

interface BackendChapter {
  id: number;
  chapter_no: number;
  title: string;
  summary: string;
  content: string;
  status: string;
  words: number;
  target_words: number;
  progress: number;
  score?: number | null;
  review?: ChapterReviewData | null;
}

interface BackendInspirationMin {
  id: number;
  seed?: string;
  title?: string;
  premise?: string;
  stakes?: string;
  conflict?: string;
  hook?: string;
  boundary?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

interface GenerationSettings {
  max_chapters: number;
  default_chapters: number;
  default_words_per_chapter: number;
  publish_min_score: number;
}

interface AutoGenerateScope {
  projectId: number;
  inspirationId: number | null;
}

interface AutoGenerateEstimate {
  accountBalance: number;
  costPerChapter: number;
  affordableChapters: number;
  remainingChapters: number;
  plannedChapters: number;
  billingMultiplier: number;
  basis: string;
}

const emptyAutoGenerateEstimate: AutoGenerateEstimate = {
  accountBalance: 0,
  costPerChapter: 0,
  affordableChapters: 0,
  remainingChapters: 0,
  plannedChapters: 0,
  billingMultiplier: 1,
  basis: '',
};

const emptyStageCompletion = topTabs.reduce((items, tab) => ({
  ...items,
  [tab.key]: false,
}), {} as StageCompletion);

const completeStagesInOrder = (raw: StageCompletion): StageCompletion => {
  let previousDone = true;
  return topTabs.reduce((items, tab) => {
    const done = Boolean(previousDone && raw[tab.key]);
    items[tab.key] = done;
    previousDone = done;
    return items;
  }, {} as StageCompletion);
};

const blockingStageFor = (tab: TopTabKey, completion: StageCompletion) => {
  const targetIndex = topTabs.findIndex((item) => item.key === tab);
  const requiredStages = topTabs.slice(0, Math.max(0, targetIndex));
  return requiredStages.find((item) => item.key !== 'review' && !completion[item.key]);
};

const inspirationSortTime = (item: BackendInspirationMin) => {
  return parseServerDateTime(item.created_at);
};

const sortWorkbenchInspirations = (items: BackendInspirationMin[] = []) => (
  [...items].sort((a, b) => inspirationSortTime(b) - inspirationSortTime(a))
);

interface WorkbenchSnapshot {
  project: BackendProject;
  chapters: BackendChapter[];
  inspirations?: BackendInspirationMin[];
  settings?: unknown[];
  characters?: unknown[];
  outlines?: unknown[];
  hooks?: unknown[];
}

interface WorkflowStageStatus {
  done: boolean;
  locked: boolean;
  count: number;
}

interface WorkflowStatusResponse {
  project_id: number;
  requested_inspiration_id?: number | null;
  active_inspiration_id?: number | null;
  stages: Record<TopTabKey, WorkflowStageStatus>;
}

interface ChapterGenerateResult {
  chapter: BackendChapter;
}

interface ChapterReviewResult {
  chapter: BackendChapter;
  review: {
    score?: number;
    dimensions?: ReviewDimension[];
    suggestions?: string[];
    problems?: ReviewProblem[];
    risk_flags?: string[];
    continuity_notes?: string[];
  };
}

interface ChapterReviseResult {
  chapter?: BackendChapter | null;
  applied: boolean;
  message: string;
}

interface CurrentUser {
  id: number;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
}

interface WorkbenchInkAccount {
  balance: number;
  frozen: number;
}

interface WorkbenchUserAssets {
  account: WorkbenchInkAccount;
}

const emptyWorkbenchAssets: WorkbenchUserAssets = {
  account: {
    balance: 0,
    frozen: 0,
  },
};

const readStoredUser = (): CurrentUser | null => {
  try {
    const raw = localStorage.getItem('lingbi-user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const authHeaders = () => {
  const token = localStorage.getItem('lingbi-token');
  return {
    'Content-Type': 'application/json',
    ...antiBrushHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const apiRequest = async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const text = await response.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!response.ok) {
    const gatewayMessage = response.status === 502 || response.status === 504
      ? 'AI生成等待超时，系统没有丢失已完成内容，请稍后重试'
      : `请求失败（${response.status}）`;
    const detail = data?.detail || data?.message;
    const detailMessage = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join('；')
        : detail && typeof detail === 'object'
          ? JSON.stringify(detail)
          : '';
    throw new Error(detailMessage || gatewayMessage);
  }
  if (text && data === null) throw new Error('服务返回格式异常，请稍后重试');
  return data as T;
};

const readResponseError = async (response: Response, fallback: string) => {
  const text = await response.text().catch(() => '');
  if (!text) return fallback;
  try {
    const data = JSON.parse(text);
    const detail = data?.detail || data?.message;
    if (typeof detail === 'string') return detail;
    if (detail) return JSON.stringify(detail);
  } catch {
    return text;
  }
  return fallback;
};

const workbenchPath = (projectId: number, inspirationId?: number | null) => (
  `/novel_project_app/projects/${projectId}/workbench${inspirationId ? `?inspiration_id=${inspirationId}` : ''}`
);

const projectChapterTargetWords = (project?: BackendProject | null) => (
  Number(project?.words_per_chapter || 0) >= 300 ? Number(project?.words_per_chapter || 0) : 0
);

const chapterTargetWords = (chapter: Chapter, project?: BackendProject | null) => (
  projectChapterTargetWords(project) || Math.max(300, Number(chapter.targetWords || 0) || 0)
);

const genericChapterTitleWords = new Set(['开局', '开始', '铺垫', '发展', '推进', '转折', '高潮', '结尾', '终局', '尾声', '收束', '回收']);
const fallbackChapterTitleCores = ['命运推门', '第一道规则', '无法回头的选择', '代价显形', '陌生盟友', '秘密松动', '旧线反咬', '敌意逼近', '规则裂缝', '真相试探', '关系反转', '代价加深', '伏笔回响', '旧账浮出', '最后筹码', '真相逼近', '选择落定'];

const stripChapterTitlePrefix = (value: string) => value
  .trim()
  .replace(/^第\s*[0-9零一二三四五六七八九十百千]+\s*[章节回]\s*[·:：、.\-\s]*/, '')
  .trim();

const isGenericChapterTitle = (value: string) => {
  const compact = stripChapterTitlePrefix(value).replace(/[\s·:：、.\-—_]+/g, '');
  if (!compact) return true;
  if (genericChapterTitleWords.has(compact)) return true;
  if (/^(被卷入主线事件|主角被卷入|主角尝试使用|尝试使用已有线索|旧线索和人物秘密|建立主角目标|回收核心伏笔|本章建立|本章推进)/.test(compact)) return true;
  return compact.length <= 4 && Array.from(genericChapterTitleWords).some((word) => compact.includes(word));
};

const cleanChapterTitleCore = (value: string) => stripChapterTitlePrefix(value)
  .split(/[。！？!?；;\n\r]/)[0]
  .replace(/^(本章|这一章|主角|他|她|他们|她们|少年|少女)/, '')
  .replace(/["“”'‘’《》#*`~]/g, '')
  .replace(/\s+/g, '')
  .replace(/^[，,、：:；;。.!！?？]+|[，,、：:；;。.!！?？]+$/g, '')
  .slice(0, 12);

const normalizeChapterTitle = (title: string, no: number, summary = '') => {
  const core = stripChapterTitlePrefix(title || '');
  if (core && !isGenericChapterTitle(core)) return core;
  const fromSummary = cleanChapterTitleCore(summary || '');
  if (fromSummary && !isGenericChapterTitle(fromSummary)) return fromSummary;
  return fallbackChapterTitleCores[(Math.max(no, 1) - 1) % fallbackChapterTitleCores.length];
};

const toChapter = (chapter: BackendChapter, fallbackChapterNo = 1): Chapter => {
  const backendId = normalizeBackendChapterId(chapter.id);
  const rawChapterNo = Number(chapter.chapter_no);
  const chapterNo = Number.isFinite(rawChapterNo) && rawChapterNo >= 1
    ? Math.round(rawChapterNo)
    : Math.max(1, fallbackChapterNo);
  return {
    id: backendId ? `chapter-${backendId}` : `chapter-pending-${chapterNo}`,
    backendId,
    index: String(chapterNo).padStart(2, '0'),
    title: normalizeChapterTitle(chapter.title, chapterNo, chapter.summary),
    progress: chapter.progress,
    status: chapter.status === 'stale'
      ? 'stale'
      : chapter.status === 'done'
        ? 'done'
        : chapter.status === 'planned'
          ? 'todo'
          : 'active',
    summary: chapter.summary,
    words: chapter.words,
    targetWords: chapter.target_words,
    content: chapter.content,
    score: chapter.score ?? chapter.review?.score ?? null,
    review: chapter.review || null,
  };
};

const hasGeneratedContent = (chapter: Chapter) => Boolean(chapter.content?.trim());

const isChapterSequenceReady = (chapter: Chapter) => hasGeneratedContent(chapter) && chapter.status !== 'stale';

const isChapterInProgress = (chapter: Chapter) => !hasGeneratedContent(chapter) && chapter.status === 'active';

const preserveStreamingChapterContent = (
  incoming: Chapter[],
  current: Chapter[],
  busyChapterIds: Set<string>,
) => incoming.map((chapter) => {
  if (hasGeneratedContent(chapter) || !busyChapterIds.has(chapter.id)) return chapter;
  const local = current.find((item) => item.id === chapter.id);
  return local && hasGeneratedContent(local)
    ? { ...chapter, content: local.content, words: local.words, progress: local.progress }
    : chapter;
});

const reviewDimensionMeta: Array<[string, string, string]> = [
  ['一致性', '一致性', '#19b890'],
  ['节奏', '节奏', '#4d8bcf'],
  ['爽点', '爽点钩子', '#bf5a08'],
  ['伏笔人设', '伏笔人设', '#7c3cff'],
];

const asReviewData = (value: unknown): ChapterReviewData => (
  value && typeof value === 'object' ? value as ChapterReviewData : {}
);

const chapterScoreValue = (chapter: Chapter) => {
  if (!hasGeneratedContent(chapter)) return 0;
  const review = asReviewData(chapter.review);
  const raw = chapter.score ?? review.score ?? 0;
  return Math.max(0, Math.min(100, Number(raw) || 0));
};

const hasCompletedChapterReview = (chapter: Chapter) => {
  if (!hasGeneratedContent(chapter)) return false;
  const review = asReviewData(chapter.review);
  return Number(chapter.score ?? review.score ?? 0) > 0;
};

const chapterScoreGrade = (value: number) => {
  if (value <= 0) return '待评';
  if (value >= 90) return 'A';
  if (value >= 86) return 'A-';
  if (value >= 80) return 'B+';
  return '待修';
};

const chapterScoreDimensions = (chapter: Chapter) => {
  if (!hasGeneratedContent(chapter)) {
    return reviewDimensionMeta.map(([label, , color]) => ({ label, score: 0, color }));
  }
  const review = asReviewData(chapter.review);
  const dimensions = Array.isArray(review.dimensions) ? review.dimensions : [];
  const fallbackScore = chapterScoreValue(chapter);
  return reviewDimensionMeta.map(([label, backendName, color]) => {
    const matched = dimensions.find((item) => item?.name === backendName || item?.name === label);
    const rawScore = Number(matched?.score ?? (dimensions.length ? 0 : fallbackScore));
    return { label, score: Math.max(0, Math.min(100, rawScore || 0)), color };
  });
};

const orderedChapters = (items: Chapter[]) => (
  [...items].sort((left, right) => Number(left.index) - Number(right.index))
);

const findNextChapterToGenerate = (items: Chapter[]) => (
  orderedChapters(items).find((chapter) => !isChapterSequenceReady(chapter))
);

const findSequentialGenerationQueue = (items: Chapter[]) => {
  const ordered = orderedChapters(items);
  const firstPendingIndex = ordered.findIndex((chapter) => !isChapterSequenceReady(chapter));
  // 中间章节缺失后，后面的旧正文没有参考这次补写的前文，必须从断点开始依次重写。
  return firstPendingIndex >= 0 ? ordered.slice(firstPendingIndex) : [];
};

const getTabFromHash = (): TopTabKey => {
  const raw = window.location.hash.replace(/^#workbench\/?/, '');
  return tabKeys.has(raw as TopTabKey) ? (raw as TopTabKey) : 'inspiration';
};

const getStatusIcon = (chapter: Chapter) => {
  if (isChapterSequenceReady(chapter)) return <Check size={14} />;
  if (isChapterInProgress(chapter)) return <span className="active-dot" />;
  return <Circle size={14} />;
};

const getInitialAiPanelLayout = (): AiPanelLayout => {
  const viewportWidth = window.innerWidth || 1440;
  const viewportHeight = window.innerHeight || 900;
  const width = Math.min(392, Math.max(320, viewportWidth - 48));
  const height = Math.min(Math.max(430, viewportHeight - 110), viewportHeight - 24);

  return {
    x: Math.max(8, viewportWidth - width),
    y: 80,
    width,
    height,
  };
};

const clampAiPanelLayout = (layout: AiPanelLayout): AiPanelLayout => {
  const minWidth = Math.min(320, Math.max(280, window.innerWidth - 16));
  const minHeight = Math.min(360, Math.max(300, window.innerHeight - 16));
  const maxWidth = Math.max(minWidth, window.innerWidth - 16);
  const maxHeight = Math.max(minHeight, window.innerHeight - 16);
  const width = Math.min(Math.max(layout.width, minWidth), maxWidth);
  const height = Math.min(Math.max(layout.height, minHeight), maxHeight);
  const x = Math.min(Math.max(layout.x, 8), Math.max(8, window.innerWidth - width - 8));
  const y = Math.min(Math.max(layout.y, 8), Math.max(8, window.innerHeight - height - 8));

  return { x, y, width, height };
};

interface WorkbenchPageProps {
  onHome: () => void;
}

const WorkbenchPage: React.FC<WorkbenchPageProps> = ({ onHome }) => {
  const [activeTab, setActiveTab] = useState<TopTabKey>(getTabFromHash);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [activeChapterId, setActiveChapterId] = useState(emptyChapter.id);
  const [project, setProject] = useState<BackendProject | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [workbenchInspirations, setWorkbenchInspirations] = useState<BackendInspirationMin[]>([]);
  const [activeInspirationId, setActiveInspirationId] = useState<number | null>(null);
  const [workbenchLoading, setWorkbenchLoading] = useState(() => Boolean(localStorage.getItem('lingbi-token')));
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() => (
    localStorage.getItem('lingbi-token') ? readStoredUser() : null
  ));
  const [liveGenerating, setLiveGenerating] = useState(false);
  const [generatingChapterId, setGeneratingChapterId] = useState<string | null>(null);
  const [finalizingChapterId, setFinalizingChapterId] = useState<string | null>(null);
  const [messageApi, messageContextHolder] = antdMessage.useMessage();
  const [score, setScore] = useState(0);
  const [revisionPrompt, setRevisionPrompt] = useState('');
  const [revisionBusy, setRevisionBusy] = useState(false);
  const [chapterSaveBusy, setChapterSaveBusy] = useState(false);
  const [chapterRevisionBusy, setChapterRevisionBusy] = useState(false);
  const [chapterRevisionNotice, setChapterRevisionNotice] = useState('');
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [reviewingChapterIds, setReviewingChapterIds] = useState<number[]>([]);
  const [autoGenerateOpen, setAutoGenerateOpen] = useState(false);
  const [autoGenerateState, setAutoGenerateState] = useState<AutoGenerateState>('checking');
  const [autoGenerateSteps, setAutoGenerateSteps] = useState<AutoGenerateStep[]>([]);
  const [autoGenerateScope, setAutoGenerateScope] = useState<AutoGenerateScope | null>(null);
  const [autoGenerateEstimate, setAutoGenerateEstimate] = useState<AutoGenerateEstimate>(emptyAutoGenerateEstimate);
  const [projectSwitcherOpen, setProjectSwitcherOpen] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiHistoryOpen, setAiHistoryOpen] = useState(false);
  const [aiPanelLayout, setAiPanelLayout] = useState<AiPanelLayout>(getInitialAiPanelLayout);
  const [workbenchAssets, setWorkbenchAssets] = useState<WorkbenchUserAssets>(emptyWorkbenchAssets);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState('');
  const [aiNotice, setAiNotice] = useState('');
  const [inspirationTitle, setInspirationTitle] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('lingbi-theme') || 'light');
  const [aiSessions, setAiSessions] = useState<AiSession[]>([
    { id: 'session-1', title: '灵感梗概调整', last: '当前会话', messages: [] },
  ]);
  const [activeAiSessionId, setActiveAiSessionId] = useState('session-1');
  const [newAiSessionConfirmOpen, setNewAiSessionConfirmOpen] = useState(false);
  const [assistantSettingsOpen, setAssistantSettingsOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveClock, setSaveClock] = useState(Date.now);
  const [stageCompletion, setStageCompletion] = useState<StageCompletion>(emptyStageCompletion);
  const [chapterPlanAvailable, setChapterPlanAvailable] = useState(false);
  const [chapterPlanGenerating, setChapterPlanGenerating] = useState(false);
  const [chapterPlanPromptDismissed, setChapterPlanPromptDismissed] = useState(false);
  const [chapterPlanMessage, setChapterPlanMessage] = useState('');
  const [maxChapters, setMaxChapters] = useState(200);
  const [generationSettings, setGenerationSettings] = useState<GenerationSettings | null>(null);
  const [inkRechargePrompt, setInkRechargePrompt] = useState<string | null>(null);
  const autoGenerateAbortRef = useRef(false);
  const aiChatThreadRef = useRef<HTMLDivElement | null>(null);
  const chapterEditorRef = useRef<HTMLTextAreaElement | null>(null);

  const scrollAiChatToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    window.requestAnimationFrame(() => {
      const node = aiChatThreadRef.current;
      if (!node) return;
      node.scrollTo({ top: node.scrollHeight, behavior });
    });
  }, []);
  const autoGenerateRequestRef = useRef<AbortController | null>(null);
  const autoGenerateScopeRef = useRef<AutoGenerateScope | null>(null);
  const projectSwitcherRef = useRef<HTMLDivElement | null>(null);
  const memoryExtractionRef = useRef<Set<number>>(new Set());
  const chapterReviewInFlightRef = useRef<Set<number>>(new Set());
  const firstChapterInitializationRef = useRef<Set<string>>(new Set());
  const firstChapterInitializationAttemptsRef = useRef<Map<string, number>>(new Map());
  const activeInspirationIdRef = useRef<number | null>(null);
  const selectedProjectIdRef = useRef<number | null>(null);
  const projectIdRef = useRef<number | null>(null);
  const workbenchInitializationRef = useRef<Promise<number | null> | null>(null);
  const workbenchInitializationErrorRef = useRef('');
  const activeChapterIdRef = useRef(emptyChapter.id);
  const lastWorkbenchLoadedAtRef = useRef(0);
  const chaptersRef = useRef<Chapter[]>([]);
  const generatingChapterIdRef = useRef<string | null>(null);
  const finalizingChapterIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeChapterIdRef.current = activeChapterId;
  }, [activeChapterId]);

  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  useEffect(() => {
    generatingChapterIdRef.current = generatingChapterId;
  }, [generatingChapterId]);

  useEffect(() => {
    finalizingChapterIdRef.current = finalizingChapterId;
  }, [finalizingChapterId]);

  const activeChapter = chapters.find((chapter) => chapter.id === activeChapterId) || chapters[0] || emptyChapter;
  const activeChapterHasContent = hasGeneratedContent(activeChapter);
  const activeChapterScore = chapterScoreValue(activeChapter);
  const activeChapterGrade = chapterScoreGrade(activeChapterScore);
  const activeScoreDimensions = chapterScoreDimensions(activeChapter);
  const activeChapterHasReview = hasCompletedChapterReview(activeChapter);
  const activeChapterBackendId = normalizeBackendChapterId(activeChapter.backendId);
  const activeChapterIsReviewing = Boolean(
    activeChapterBackendId && reviewingChapterIds.includes(activeChapterBackendId),
  );
  // 评分完成是章节生成链路的最终完成信号。即使某次流式请求的 finally、页面切换
  // 或重新拉取发生竞态，也不能让已有完整评分的章节继续显示“实时生成中”。
  const activeChapterReviewComplete = hasCompletedChapterReview(activeChapter);
  const activeChapterIsFinalizing = finalizingChapterId === activeChapter.id
    && !activeChapterReviewComplete;
  const activeChapterIsGenerating = liveGenerating
    && generatingChapterId === activeChapter.id
    && !activeChapterIsFinalizing
    && !activeChapterReviewComplete;
  const chapterGenerationIndicator = activeChapterIsFinalizing
    ? { className: 'reviewing', text: aiNotice || '正文生成完成，正在保存' }
    : activeChapterIsGenerating
      ? { className: 'active', text: '实时生成中' }
      : activeChapterHasContent
        ? { className: 'complete', text: '生成完成' }
        : { className: 'idle', text: '等待生成' };

  useEffect(() => {
    if (!activeChapterIsGenerating) return;
    window.requestAnimationFrame(() => {
      const editor = chapterEditorRef.current;
      if (editor) editor.scrollTop = editor.scrollHeight;
    });
  }, [activeChapter.content, activeChapterIsGenerating]);
  const visibleProject = project || emptyProjectMeta;
  const activeChapterTargetWords = chapterTargetWords(activeChapter, project);
  const saveStatusText = useMemo(() => {
    if (!lastSavedAt) return '新灵感尚未保存';
    const seconds = Math.max(0, Math.floor((saveClock - lastSavedAt) / 1000));
    if (seconds < 5) return '刚刚保存';
    if (seconds < 60) return `${seconds} 秒前保存`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} 分钟前保存`;
    const hours = Math.floor(minutes / 60);
    return `${hours} 小时前保存`;
  }, [lastSavedAt, saveClock]);
  const documentName = (
    inspirationTitle
    || project?.title
    || project?.file_name
    || emptyProjectMeta.title
    || emptyProjectMeta.fileName
  )
    .replace(/[《》]/g, '')
    .replace(/\.lumiu\.md$/i, '')
    .trim();
  const generatedChapterCount = useMemo(() => {
    const firstPendingIndex = orderedChapters(chapters).findIndex((chapter) => !isChapterSequenceReady(chapter));
    return firstPendingIndex >= 0 ? firstPendingIndex : chapters.length;
  }, [chapters]);
  const narrativePerspective = String(
    project?.settings?.narrative_perspective
    || project?.settings?.perspective
    || '',
  ).trim() || '视角未设置';
  const plannedChapterCount = Math.max(chapters.length, project?.chapter_target || chapters.length || 50);
  const nextChapterToGenerate = useMemo(() => findNextChapterToGenerate(chapters), [chapters]);
  const wordCount = useMemo(() => chapters.reduce((sum, chapter) => sum + chapter.words, 0), [chapters]);
  const targetWordCount = useMemo(() => {
    const projectTotal = (project?.chapter_target || 0) * (project?.words_per_chapter || 0);
    if (projectTotal > 0) return projectTotal;
    const chapterTotal = chapters.reduce((sum, chapter) => sum + (chapter.targetWords || 0), 0);
    return chapterTotal || 0;
  }, [chapters, project?.chapter_target, project?.words_per_chapter]);
  const autoGenerateQueueCandidates = useMemo(() => findSequentialGenerationQueue(chapters), [chapters]);
  const fallbackAutoGenerateEstimate = useMemo(() => {
    const accountBalance = Number(workbenchAssets.account.balance || 0);
    const targetWords = chapterTargetWords(autoGenerateQueueCandidates[0] || activeChapter, project) || project?.words_per_chapter || 1000;
    const costPerChapter = Math.max(1, Math.ceil(((Math.max(300, targetWords) * 1.8 + 1800) * 2) / 200));
    const affordableChapters = Math.floor(accountBalance / costPerChapter);
    const remainingChapters = Math.max(0, autoGenerateQueueCandidates.length);
    return {
      accountBalance,
      affordableChapters,
      costPerChapter,
      plannedChapters: Math.min(affordableChapters, remainingChapters),
      remainingChapters,
      billingMultiplier: 2,
      basis: '前端兜底估算',
    };
  }, [activeChapter, autoGenerateQueueCandidates, project, workbenchAssets.account.balance]);
  const autoGenerateDoneCount = autoGenerateSteps.filter((item) => item.status === 'done').length;
  const autoGenerateActiveStep = autoGenerateSteps.find((item) => item.status === 'running' || item.status === 'reviewing');
  const autoGenerateRunningForCurrent = autoGenerateState === 'running'
    && autoGenerateScope?.projectId === project?.id
    && autoGenerateScope?.inspirationId === activeInspirationId;
  const autoGenerateBlockedByOtherTask = revisionBusy || chapterRevisionBusy || chapterPlanGenerating;

  useEffect(() => {
    if (
      !liveGenerating
      || !generatingChapterId
      || autoGenerateRunningForCurrent
      || chapterRevisionBusy
      || chapterPlanGenerating
    ) return;
    const generatedChapter = chapters.find((chapter) => chapter.id === generatingChapterId);
    if (!generatedChapter || !hasCompletedChapterReview(generatedChapter)) return;

    // 后端章节和评分均已落库时，本地生成任务已经没有继续占用页面的理由。
    // 统一释放临时标记，避免顶部状态、生成按钮和保存按钮一直卡在忙碌态。
    setLiveGenerating(false);
    setGeneratingChapterId(null);
    setFinalizingChapterId(null);
    setRevisionBusy(false);
  }, [
    autoGenerateRunningForCurrent,
    chapterPlanGenerating,
    chapterRevisionBusy,
    chapters,
    generatingChapterId,
    liveGenerating,
  ]);

  useEffect(() => {
    apiRequest<GenerationSettings>('/novel_project_app/generation-settings')
      .then((settings) => {
        setGenerationSettings(settings);
        setMaxChapters(Math.max(3, Number(settings.max_chapters) || 200));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const onResize = () => setAiPanelLayout((layout) => clampAiPanelLayout(layout));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lingbi-theme', theme);
  }, [theme]);

  useEffect(() => {
    const timer = window.setInterval(() => setSaveClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const handleAiFailure = useCallback((content: string) => {
    const text = String(content || '').trim();
    if (/(墨水.{0,8}(不足|不够|耗尽)|余额.{0,8}(不足|不够|耗尽)|insufficient.{0,16}(ink|balance)|not enough.{0,16}(ink|balance))/i.test(text)) {
      setInkRechargePrompt(text || '墨水余额不足，请充值后继续创作。');
    }
  }, []);

  const setToast = useCallback((content: string) => {
    const text = String(content || '').trim();
    if (!text) return;
    handleAiFailure(text);
    messageApi.open({
      type: /失败|错误|异常|不足|请先|不能|暂无|超时|中断/.test(text) ? 'warning' : 'success',
      content: text,
      duration: 2.6,
      className: 'workbench-message-toast',
    });
  }, [handleAiFailure, messageApi]);

  const requireLogin = useCallback(() => {
    if (localStorage.getItem('lingbi-token') && currentUser) return true;
    Modal.confirm({
      title: '请先登录',
      content: '登录后才能使用 AI 生成，生成内容会保存到你的账号并按实际用量计费。',
      okText: '去登录',
      cancelText: '暂不登录',
      centered: true,
      onOk: () => { window.location.hash = '#login'; },
    });
    return false;
  }, [currentUser]);

  useEffect(() => {
    activeInspirationIdRef.current = activeInspirationId;
  }, [activeInspirationId]);

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    projectIdRef.current = project?.id || null;
  }, [project?.id]);

  useEffect(() => {
    autoGenerateScopeRef.current = autoGenerateScope;
  }, [autoGenerateScope]);

  useEffect(() => {
    if (!projectSwitcherOpen) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (projectSwitcherRef.current?.contains(target)) return;
      setProjectSwitcherOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProjectSwitcherOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [projectSwitcherOpen]);

  useEffect(() => {
    setScore(activeChapterScore);
  }, [activeChapterScore]);

  const loadWorkbenchAssets = useCallback(async () => {
    if (!localStorage.getItem('lingbi-token')) {
      setWorkbenchAssets(emptyWorkbenchAssets);
      setAssetsError('');
      return;
    }
    setAssetsLoading(true);
    setAssetsError('');
    try {
      setWorkbenchAssets(await apiRequest<WorkbenchUserAssets>('/users_app/me/assets'));
    } catch (error) {
      setAssetsError(error instanceof Error ? error.message : '资产同步失败');
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      void loadWorkbenchAssets();
    } else {
      setWorkbenchAssets(emptyWorkbenchAssets);
      setAssetsError('');
    }
  }, [currentUser, loadWorkbenchAssets]);

  const refreshWorkflowStatus = useCallback(async (projectId?: number | null, inspirationId?: number | null) => {
    const currentProjectId = projectId || projectIdRef.current;
    const currentInspirationId = inspirationId || activeInspirationIdRef.current;
    if (!currentProjectId || !currentInspirationId || !localStorage.getItem('lingbi-token')) return;
    try {
      const status = await apiRequest<WorkflowStatusResponse>(
        `/novel_project_app/projects/${currentProjectId}/workflow-status?inspiration_id=${currentInspirationId}`,
      );
      setStageCompletion((items) => {
        const next = { ...items };
        topTabs.forEach((tab) => {
          next[tab.key] = Boolean(status.stages[tab.key]?.done);
        });
        return next;
      });
    } catch (error) {
      setAiNotice(error instanceof Error ? error.message : '流程状态同步失败');
    }
  }, []);

  const loadWorkbenchOverview = useCallback(async (): Promise<number | null> => {
    if (!localStorage.getItem('lingbi-token')) {
      setWorkbenchLoading(false);
      setCurrentUser(null);
      setProject(null);
      setSelectedProjectId(null);
      setWorkbenchInspirations([]);
      setActiveInspirationId(null);
      setInspirationTitle('');
      setStageCompletion(emptyStageCompletion);
      setChapters([]);
      setActiveChapterId(emptyChapter.id);
      setLastSavedAt(null);
      return null;
    }

    setWorkbenchLoading(true);
    try {
      const [user, projectItems] = await Promise.all([
        apiRequest<CurrentUser>('/users_app/me'),
        apiRequest<BackendProject[]>('/novel_project_app/projects'),
      ]);
      setCurrentUser(user);
      localStorage.setItem('lingbi-user', JSON.stringify(user));

      let projects = projectItems;
      if (!projects.length) {
        const createdProject = await apiRequest<BackendProject>('/novel_project_app/projects', {
          method: 'POST',
          body: JSON.stringify({
            title: '未命名新书',
            platform: '通用',
            genre: '待选择',
          }),
        });
        projects = [createdProject];
      }
      const initialProject = projects[0] || null;
      if (!initialProject) throw new Error('工作台项目初始化失败，请刷新后重试');

      const inspirationItems = await apiRequest<BackendInspirationMin[]>(
        `/novel_project_app/projects/${initialProject.id}/inspirations`,
      );
      const nextInspirations = sortWorkbenchInspirations(inspirationItems);
      const workflowSourceId = initialProject.settings?._workflow?.source_inspiration_id;
      const activeInspiration = nextInspirations.find((item) => Number(item.id) === Number(workflowSourceId))
        || nextInspirations[0];
      const nextInspirationId = activeInspiration?.id || null;

      setSelectedProjectId(initialProject.id);
      selectedProjectIdRef.current = initialProject.id;
      setProject(initialProject);
      projectIdRef.current = initialProject.id;
      setWorkbenchInspirations(nextInspirations);
      setActiveInspirationId(nextInspirationId);
      activeInspirationIdRef.current = nextInspirationId;
      setInspirationTitle(activeInspiration?.title || '');
      setStageCompletion({ ...emptyStageCompletion, inspiration: Boolean(activeInspiration) });
      setChapterPlanAvailable(false);
      setChapters([]);
      setActiveChapterId(emptyChapter.id);
      lastWorkbenchLoadedAtRef.current = 0;
      workbenchInitializationErrorRef.current = '';
      setAiNotice('灵感目录已同步');
      return initialProject.id;
    } catch (error) {
      const message = error instanceof Error ? error.message : '工作台数据同步失败';
      workbenchInitializationErrorRef.current = message;
      setAiNotice(message);
      return null;
    } finally {
      setWorkbenchLoading(false);
    }
  }, []);

  const loadWorkbench = useCallback(async (inspirationId?: number | null, projectId?: number | null): Promise<number | null> => {
    if (!localStorage.getItem('lingbi-token')) {
      setWorkbenchLoading(false);
      setCurrentUser(null);
      setProject(null);
      setSelectedProjectId(null);
      setWorkbenchInspirations([]);
      setActiveInspirationId(null);
      setInspirationTitle('');
      setStageCompletion(emptyStageCompletion);
      setChapters([]);
      setActiveChapterId(emptyChapter.id);
      setLastSavedAt(null);
      return null;
    }
    if (inspirationId === null) {
      setWorkbenchLoading(false);
      setActiveInspirationId(null);
      activeInspirationIdRef.current = null;
      setInspirationTitle('《未命名新书》');
      setStageCompletion(emptyStageCompletion);
      setChapterPlanAvailable(false);
      setChapters([]);
      setActiveChapterId(emptyChapter.id);
      setScore(0);
      setChapterPlanPromptDismissed(false);
      setLastSavedAt(null);
      setAiNotice('已切换到未保存的新灵感');
      return projectIdRef.current;
    }
    const requestedProjectId = projectId || selectedProjectIdRef.current || projectIdRef.current;
    const scopeChanged = Boolean(
      (projectId && projectIdRef.current && Number(projectId) !== Number(projectIdRef.current))
      || (inspirationId && activeInspirationIdRef.current && Number(inspirationId) !== Number(activeInspirationIdRef.current))
    );
    const shouldBlockWorkbench = !projectIdRef.current || scopeChanged;
    if (shouldBlockWorkbench) setWorkbenchLoading(true);
    try {
      let targetProjectId = requestedProjectId;
      if (!targetProjectId) {
        return loadWorkbenchOverview();
      }
      if (!targetProjectId) throw new Error('工作台项目初始化失败，请刷新后重试');
      setSelectedProjectId(targetProjectId);
      const statusInspirationId = inspirationId || activeInspirationIdRef.current;
      const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(targetProjectId, statusInspirationId));
      const nextChapters = preserveStreamingChapterContent(
        snapshot.chapters.map((chapter, index) => toChapter(chapter, index + 1)),
        chaptersRef.current,
        new Set([generatingChapterIdRef.current, finalizingChapterIdRef.current].filter(Boolean) as string[]),
      );
      const workflowSourceId = statusInspirationId || snapshot.project.settings?._workflow?.source_inspiration_id || snapshot.inspirations?.[0]?.id;
      const activeInspiration = snapshot.inspirations?.find((item) => Number(item.id) === Number(workflowSourceId));
      setWorkbenchInspirations(sortWorkbenchInspirations(snapshot.inspirations || []));
      setActiveInspirationId(workflowSourceId || null);
      activeInspirationIdRef.current = workflowSourceId || null;
      setInspirationTitle(activeInspiration?.title || '');
      setProject(snapshot.project);
      projectIdRef.current = snapshot.project.id;
      lastWorkbenchLoadedAtRef.current = Date.now();
      workbenchInitializationErrorRef.current = '';
      setChapterPlanAvailable(Boolean(snapshot.outlines?.length));
      const snapshotPublish = snapshot.project.publish || {};
      const contentChapters = nextChapters.filter(hasGeneratedContent);
      const publishMinScore = Number(generationSettings?.publish_min_score || 80);
      setStageCompletion(completeStagesInOrder({
        inspiration: Boolean(activeInspiration && snapshot.inspirations?.length),
        settings: Boolean(snapshot.settings?.length),
        characters: Boolean(snapshot.characters?.length),
        outline: Boolean(snapshot.outlines?.length),
        chapters: contentChapters.length > 0,
        review: contentChapters.length > 0 && contentChapters.every((chapter) => (
          hasCompletedChapterReview(chapter) && chapterScoreValue(chapter) >= publishMinScore
        )),
        publish: snapshotPublish.status === '已上架'
          && Boolean(snapshotPublish.published_at)
          && Boolean(snapshotPublish.slug),
      }));
      setChapters(nextChapters);
      setChapterPlanPromptDismissed(false);
      if (nextChapters.length) {
        const active = nextChapters.find((chapter) => chapter.id === activeChapterIdRef.current)
          || nextChapters.find((chapter) => Number(chapter.index) === Number(snapshot.project.active_chapter_no || 1))
          || nextChapters[0];
        setActiveChapterId(active.id);
        setScore(chapterScoreValue(active));
      } else {
        setActiveChapterId(emptyChapter.id);
        setScore(0);
      }
      setAiNotice('工作台数据已同步');
      return snapshot.project.id;
    } catch (error) {
      const message = error instanceof Error ? error.message : '工作台数据同步失败';
      workbenchInitializationErrorRef.current = message;
      setAiNotice(message);
      return null;
    } finally {
      setWorkbenchLoading(false);
    }
  }, [generationSettings?.publish_min_score, loadWorkbenchOverview]);

  const initializeWorkbench = useCallback(() => {
    if (projectIdRef.current) return Promise.resolve(projectIdRef.current);
    if (workbenchInitializationRef.current) return workbenchInitializationRef.current;
    const pending = loadWorkbenchOverview();
    workbenchInitializationRef.current = pending;
    void pending.finally(() => {
      if (workbenchInitializationRef.current === pending) {
        workbenchInitializationRef.current = null;
      }
    });
    return pending;
  }, [loadWorkbenchOverview]);

  const ensureWorkbenchReady = useCallback(async () => {
    if (!localStorage.getItem('lingbi-token')) throw new Error('请先登录后再使用工作台');
    if (projectIdRef.current) return projectIdRef.current;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const readyProjectId = await initializeWorkbench();
      if (readyProjectId) return readyProjectId;
      if (attempt < 2) {
        await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
      }
    }

    const detail = workbenchInitializationErrorRef.current.trim();
    throw new Error(detail
      ? `工作台暂时无法连接，系统已自动重试：${detail}`
      : '工作台暂时无法连接，系统已自动重试，请稍后再试');
  }, [initializeWorkbench]);

  useEffect(() => {
    void ensureWorkbenchReady().catch((error) => {
      setAiNotice(error instanceof Error ? error.message : '工作台初始化失败');
    });
  }, [ensureWorkbenchReady]);

  useEffect(() => {
    if (activeTab === 'inspiration' || activeInspirationId || workbenchLoading) return;
    if (localStorage.getItem('lingbi-token') && !project) return;
    setActiveTab('inspiration');
    window.location.hash = '#workbench/inspiration';
    if (project) setToast('当前灵感池为空，请先生成或保存灵感');
  }, [activeInspirationId, activeTab, project, setToast, workbenchLoading]);

  useEffect(() => {
    void refreshWorkflowStatus(project?.id || null, activeInspirationId);
  }, [activeInspirationId, project?.id, refreshWorkflowStatus]);

  useEffect(() => {
    if (
      activeTab === 'chapters'
      && localStorage.getItem('lingbi-token')
      && projectIdRef.current
      && activeInspirationIdRef.current
      && Date.now() - lastWorkbenchLoadedAtRef.current > 15_000
    ) {
      void loadWorkbench(activeInspirationIdRef.current);
    }
  }, [activeInspirationId, activeTab, loadWorkbench, project?.id]);

  const selectInspirationOverview = useCallback((inspirationId: number, title?: string) => {
    if (Number(inspirationId) === Number(activeInspirationIdRef.current)) return;
    setActiveInspirationId(inspirationId);
    activeInspirationIdRef.current = inspirationId;
    setInspirationTitle(title || '');
    setStageCompletion({ ...emptyStageCompletion, inspiration: true });
    setChapterPlanAvailable(false);
    setChapters([]);
    setActiveChapterId(emptyChapter.id);
    lastWorkbenchLoadedAtRef.current = 0;
    setAiNotice('已切换灵感，正在同步流程状态');
  }, []);

  const goLogin = () => {
    window.location.hash = '#login';
  };

  const goRegister = () => {
    window.location.hash = '#register';
  };

  const startNewAiSession = () => {
    const nextId = `session-${Date.now()}`;
    setAiSessions((items) => [{ id: nextId, title: '新会话', last: '刚刚', messages: [] }, ...items]);
    setActiveAiSessionId(nextId);
    setRevisionPrompt('');
    setAiNotice('新的 AI 会话已开始');
  };

  const requestNewAiSession = () => {
    const current = aiSessions.find((item) => item.id === activeAiSessionId);
    if (current?.messages.length) {
      setNewAiSessionConfirmOpen(true);
      return;
    }
    startNewAiSession();
  };

  const fillPresetPrompt = (prompt: string) => {
    setRevisionPrompt(prompt);
  };

  const replaceLastAssistantMessage = (sessionId: string, text: string) => {
    setAiSessions((items) => items.map((item) => {
      if (item.id !== sessionId) return item;
      const messages = [...item.messages];
      const lastIndex = messages.length - 1;
      if (lastIndex >= 0 && messages[lastIndex].role === 'assistant') {
        messages[lastIndex] = { role: 'assistant', text };
      }
      return { ...item, last: '刚刚', messages };
    }));
  };

  const buildAssistantContext = (prompt: string) => {
    const clip = (value: string | undefined, max = 900) => {
      const text = (value || '').replace(/\s+/g, ' ').trim();
      return text.length > max ? `${text.slice(0, max)}...` : text;
    };
    const currentScope = stageScopeLabels[activeTab];
    const chapterContext = activeTab === 'chapters' || activeTab === 'review'
      ? [
          `当前章节：第 ${activeChapter.index} 章《${activeChapter.title || '未命名'}》`,
          `章节摘要：${clip(activeChapter.summary, 500) || '暂无'}`,
          `章节正文片段：${clip(activeChapter.content, 1400) || '暂无正文'}`,
          `当前评分：${activeChapterScore || 0}/100`,
        ].join('\n')
      : '';
    return [
      '你是“灵笔”小说创作工作台里的右侧 AI 助手。',
      '重要规则：只回答用户问题，只给建议或可复制文本，不要声称已经修改页面、保存数据、发布作品，也不要要求前端替用户自动改内容。',
      '回答必须围绕当前作品和当前环节；如果问题明显超出当前环节，请说明不在当前环节处理范围内，并给出应该去哪个环节处理。',
      '如果发现关键问题，请用“关键问题”列出；如果给出可替换文本或修改后内容，请单独用“可复制内容”标出来，方便用户复制。',
      '',
      `当前作品：${documentName || visibleProject.title || '未命名作品'}`,
      `当前环节：${currentScope}`,
      `当前环节说明：${aiSceneConfig.tipBody}`,
      `题材/平台：${visibleProject.genre || '未设置'} / ${visibleProject.platform || '通用'}`,
      `章节目标字数：${projectChapterTargetWords(project) || activeChapterTargetWords || '未设置'}`,
      chapterContext,
      '',
      `用户问题：${prompt}`,
    ].filter(Boolean).join('\n');
  };

  const askWorkbenchAssistant = async () => {
    if (!requireLogin()) return;
    const prompt = revisionPrompt.trim();
    if (!prompt || assistantBusy) return;
    if (!project) {
      setAiNotice('请先登录并同步工作台数据');
      return;
    }

    const sessionId = activeAiSessionId;
    const currentSession = aiSessions.find((item) => item.id === sessionId);
    const contextMessage = buildAssistantContext(prompt);
    const historyMessages = (currentSession?.messages || []).slice(-8).map((message) => ({
      role: message.role,
      content: message.text.slice(0, 3500),
    }));

    setAssistantBusy(true);
    setRevisionPrompt('');
    setAiNotice('AI助手正在回答');
    setAiSessions((items) => items.map((item) => (
      item.id === sessionId
        ? {
            ...item,
            title: item.title === '新会话' || item.title === '灵感梗概调整' ? `${stageScopeLabels[activeTab]}问答` : item.title,
            last: '刚刚',
            messages: [
              ...item.messages,
              { role: 'user', text: prompt },
              { role: 'assistant', text: '正在结合当前环节分析...' },
            ],
          }
        : item
    )));

    try {
      const response = await fetch(`${apiBaseUrl}/ai_chat/stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          user_id: currentUser?.id,
          messages: [
            ...historyMessages,
            { role: 'user', content: contextMessage },
          ],
        }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await readResponseError(response, 'AI助手连接失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let answer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        answer += decoder.decode(value, { stream: true });
        replaceLastAssistantMessage(sessionId, answer || '正在结合当前环节分析...');
      }
      answer += decoder.decode();
      replaceLastAssistantMessage(sessionId, answer.trim() || '这次没有收到有效回复，请稍后重试。');
      setAiNotice('AI助手已回复');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'AI助手回复失败';
      replaceLastAssistantMessage(sessionId, message);
      setAiNotice(message);
      setToast(message);
    } finally {
      setAssistantBusy(false);
      void loadWorkbenchAssets();
    }
  };

  const copyTextWithFallback = async (text: string) => {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // 浏览器拒绝剪贴板权限时继续使用兼容方案。
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  };

  const copyAiMessage = async (text: string) => {
    try {
      const copied = await copyTextWithFallback(text);
      if (!copied) throw new Error('copy failed');
      setToast('已复制到剪贴板');
      setAiNotice('已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动选择文本');
      setAiNotice('复制失败，请手动选择文本');
    }
  };

  const extractChapterMemory = useCallback((chapterId: number, instruction: string) => {
    if (memoryExtractionRef.current.has(chapterId)) return Promise.resolve();
    memoryExtractionRef.current.add(chapterId);
    return apiRequest(`/novel_project_app/chapters/${chapterId}/extract-memory`, {
      method: 'POST',
      body: JSON.stringify({ instruction }),
    }).catch(() => undefined).finally(() => {
      memoryExtractionRef.current.delete(chapterId);
      void loadWorkbenchAssets();
    });
  }, [loadWorkbenchAssets]);

  const handleStageCompletionChange = useCallback((tab: TopTabKey, done: boolean) => {
    setStageCompletion((items) => {
      if (items[tab] === done) return items;
      if (done) setLastSavedAt(Date.now());
      const next = { ...items, [tab]: done };
      if (!done) {
        const tabIndex = topTabs.findIndex((item) => item.key === tab);
        topTabs.slice(tabIndex + 1).forEach((item) => { next[item.key] = false; });
      }
      return completeStagesInOrder(next);
    });
  }, []);

  const switchTab = (tab: TopTabKey, _force = false) => {
    if (tab !== 'inspiration' && !activeInspirationIdRef.current && !_force) {
      setToast('请先生成或保存灵感，再进入后续步骤');
      setActiveTab('inspiration');
      window.location.hash = '#workbench/inspiration';
      return;
    }
    const blockingStage = blockingStageFor(tab, stageCompletion);
    if (blockingStage && !_force) {
      const targetLabel = topTabs.find((item) => item.key === tab)?.label || '下一步';
      setToast(`请先完成${blockingStage.label}，再进入${targetLabel}`);
      return;
    }
    if (tab === 'chapters' && activeTab !== 'chapters' && localStorage.getItem('lingbi-token')) {
      setWorkbenchLoading(true);
    }
    setActiveTab(tab);
    window.location.hash = `#workbench/${tab}`;
    trackEvent({
      event: 'workbench_tab_switch',
      page: `#workbench/${tab}`,
      module: 'workbench',
      action: 'switch_tab',
      metadata: {
        tab,
        projectId: projectIdRef.current,
        inspirationId: activeInspirationIdRef.current,
      },
    });
  };

  const switchWorkbenchInspiration = async (inspirationId: number) => {
    if (!project || Number(inspirationId) === Number(activeInspirationId)) {
      setProjectSwitcherOpen(false);
      return;
    }
    setProjectSwitcherOpen(false);
    setRevisionPrompt('');
    setReviewingChapterIds([]);
    setChapterPlanPromptDismissed(false);
    setChapterPlanMessage('');
    const nextInspiration = workbenchInspirations.find((item) => Number(item.id) === Number(inspirationId));
    if (activeTab === 'inspiration') {
      selectInspirationOverview(inspirationId, nextInspiration?.title);
    } else {
      setAiNotice('正在切换作品并同步工作台状态');
      await loadWorkbench(inspirationId, project.id);
    }
    setToast('已切换作品');
  };

  const planChaptersFromWorkbench = async () => {
    if (!requireLogin()) return;
    if (!project || !activeInspirationId || chapterPlanGenerating) {
      setChapterPlanMessage(project ? '当前作品还没有同步到灵感，请先保存灵感。' : '请先登录并同步工作台数据。');
      return;
    }
    setChapterPlanGenerating(true);
    setChapterPlanMessage('正在把大纲写入章节计划...');
    setRevisionBusy(true);
    try {
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${project.id}/chapters/plan-stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          inspiration_id: activeInspirationId,
          target_words: project.words_per_chapter || 1000,
        }),
      });
      if (!response.ok || !response.body) {
        throw new Error(await readResponseError(response, '章节计划生成失败'));
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneReceived = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === 'field' && event.field === 'chapter') {
            const chapter = event.value || {};
            setChapterPlanMessage(`正在生成第 ${String(chapter.chapter_no || Number(event.index || 0) + 1).padStart(2, '0')} 章计划...`);
          }
          if (event.type === 'done') {
            doneReceived = true;
          }
          if (event.type === 'error') {
            throw new Error(event.message || '章节计划生成失败');
          }
        }
      }
      if (!doneReceived) throw new Error('章节计划生成结果为空');
      trackEvent({
        event: 'chapter_plan_generate',
        page: '#workbench/outline',
        module: 'chapter',
        action: 'plan',
        metadata: {
          projectId: project.id,
          inspirationId: activeInspirationId,
        },
      });
      setChapterPlanMessage('章节计划已生成，正在准备第 1 章正文...');
      const snapshot = await apiRequest<WorkbenchSnapshot>(workbenchPath(project.id, activeInspirationId));
      const plannedChapters = snapshot.chapters.map((chapter, index) => toChapter(chapter, index + 1));
      const firstChapter = plannedChapters.find((chapter) => Number(chapter.index) === 1) || plannedChapters[0];
      if (!firstChapter) {
        await loadWorkbench(activeInspirationId);
        setChapterPlanMessage('');
        setToast('章节计划已生成并保存');
        return;
      }

      setChapters(plannedChapters);
      setActiveChapterId(firstChapter.id);
      setStageCompletion((items) => ({ ...items, outline: true, chapters: plannedChapters.some(hasGeneratedContent) }));
      setChapterPlanGenerating(false);

      if (hasGeneratedContent(firstChapter)) {
        setRevisionBusy(false);
        setLiveGenerating(false);
        setChapterPlanMessage('');
        if (hasCompletedChapterReview(firstChapter)) {
          setToast('章节计划已生成，第 1 章正文和评分已保留');
        } else {
          void reviewGeneratedChapter(
            firstChapter,
            '第 1 章正文已存在，后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
            { scope: { projectId: project.id, inspirationId: activeInspirationId } },
          );
          setToast('章节计划已生成，第 1 章正文已保留；后台正在自动评分');
        }
        return;
      }

      setGeneratingChapterId(firstChapter.id);
      setFinalizingChapterId(null);
      setLiveGenerating(true);
      setAiNotice('章节计划已生成，AI正在生成第 1 章正文');
      const result = await generateChapterWithStatus({
        targetProjectId: project.id,
        inspirationId: activeInspirationId,
        chapterNo: 1,
        targetWords: chapterTargetWords(firstChapter, project),
        instruction: '章节计划生成后默认生成第一章正文：严格按照第1章标题、摘要、故事设定、人物关系和大纲目标展开，开篇要有明确钩子、冲突和继续阅读动力。',
        onStatus: (message) => {
          setChapterPlanMessage(message);
          setAiNotice(message);
        },
        onChunk: (content, reset = false) => {
          setChapters((items) => items.map((chapter) => (
            Number(chapter.index) === 1
              ? { ...chapter, content: reset ? '' : `${chapter.content || ''}${content}`, words: (reset ? '' : `${chapter.content || ''}${content}`).length }
              : chapter
          )));
        },
        onContentComplete: (message) => {
          setLiveGenerating(false);
          setFinalizingChapterId(firstChapter.id);
          setAiNotice(message);
          setChapterPlanMessage(message);
        },
      });
      const generatedFirstChapter = toChapter(result.chapter);
      setChapters((items) => {
        const base = items.length ? items : plannedChapters;
        const updated = base.map((chapter) => (chapter.id === firstChapter.id ? generatedFirstChapter : chapter));
        setStageCompletion((current) => ({ ...current, chapters: updated.some(hasGeneratedContent) }));
        return updated;
      });
      setActiveChapterId(generatedFirstChapter.id);
      setScore(chapterScoreValue(generatedFirstChapter));
      setStageCompletion((items) => ({ ...items, chapters: true }));
      setLastSavedAt(Date.now());
      if (result.chapter.id) {
        void extractChapterMemory(result.chapter.id, '章节计划生成后的第一章，抽取后续写作必须记住的事实、人物状态、事件结果和伏笔');
      }
      setRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      setChapterPlanMessage('');
      setAiNotice('第 1 章正文已生成并保存，后台正在自动评分');
      setToast('章节计划和第 1 章正文已生成；后台正在自动评分');
      void reviewGeneratedChapter(
        generatedFirstChapter,
        '第 1 章生成并保存后后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
        { scope: { projectId: project.id, inspirationId: activeInspirationId } },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '章节计划生成失败';
      setChapterPlanMessage(message);
      setAiNotice(message);
      handleAiFailure(message);
      try {
        await loadWorkbench(activeInspirationId);
      } catch {
        // 流式连接可能断开，但后端通常已先落库；这里尽量重新拉取已保存章节。
      }
    } finally {
      setChapterPlanGenerating(false);
      setRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      void loadWorkbenchAssets();
    }
  };

  const updateChapterContent = (content: string) => {
    const words = content.replace(/\s/g, '').length;
    setChapters((items) =>
      items.map((chapter) =>
        chapter.id === activeChapter.id
          ? { ...chapter, content, words, targetWords: activeChapterTargetWords, progress: Math.min(100, Math.round((words / activeChapterTargetWords) * 100)) }
          : chapter,
      ),
    );
  };

  const saveChapterAndOpenReview = async () => {
    if (
      !activeChapter
      || chapterSaveBusy
      || chapterRevisionBusy
      || activeChapterIsGenerating
      || activeChapterIsFinalizing
    ) return;
    if (!hasGeneratedContent(activeChapter)) {
      setAiNotice('请先生成当前章节正文');
      setToast('请先生成当前章节正文');
      return;
    }
    const chapterId = normalizeBackendChapterId(activeChapter.backendId);
    if (!chapterId) {
      setAiNotice('当前章节尚未同步到后端');
      setToast('当前章节尚未同步到后端，请刷新后重试');
      return;
    }

    setChapterSaveBusy(true);
    setAiNotice(`正在保存第 ${activeChapter.index} 章`);
    try {
      const saved = await apiRequest<BackendChapter>(`/novel_project_app/chapters/${chapterId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: activeChapter.title,
          summary: activeChapter.summary,
          content: activeChapter.content,
          status: 'drafting',
          target_words: activeChapterTargetWords,
        }),
      });
      const nextChapter = toChapter(saved);
      setChapters((items) => items.map((chapter) => (chapter.id === activeChapter.id ? nextChapter : chapter)));
      setActiveChapterId(nextChapter.id);
      setStageCompletion((items) => ({ ...items, chapters: true }));
      setAiNotice('章节已保存，进入审稿');
      setToast('章节已保存，进入审稿');
      switchTab('review', true);
      // 保存成功就是进入下一步的唯一前置条件；自动评分独立运行，不阻塞保存或页面切换。
      void loadWorkbench(activeInspirationIdRef.current);
    } catch (error) {
      const message = error instanceof Error ? error.message : '章节保存失败';
      setAiNotice(message);
      setToast(message);
    } finally {
      setChapterSaveBusy(false);
    }
  };

  async function reviewGeneratedChapter(
    chapter: Chapter,
    instruction = '章节生成后后台自动评分：重点检查节奏、爽点、金手指价值、伏笔和人设稳定性。',
    options: { manual?: boolean; scope?: AutoGenerateScope; retries?: number } = {},
  ) {
    const chapterId = normalizeBackendChapterId(chapter.backendId);
    if (!chapterId) {
      if (options.manual) setAiNotice('当前章节尚未同步到后端');
      return false;
    }
    // 评分忙碌状态不能阻止另一章评分。全自动生成或第一章初始化可能仍在收尾，
    // 旧逻辑会把当前章评分静默跳过，且之后不再补偿。
    // 这里只按章节去重，不同章节的评分可以正常进入后端队列。
    if (chapterReviewInFlightRef.current.has(chapterId)) {
      if (options.manual) setAiNotice(`第 ${chapter.index} 章正在评分中`);
      return false;
    }
    chapterReviewInFlightRef.current.add(chapterId);
    setReviewingChapterIds((items) => (items.includes(chapterId) ? items : [...items, chapterId]));
    if (options.manual) setAiNotice(`正在评分第 ${chapter.index} 章`);
    const maxAttempts = Math.max(1, options.retries ?? (options.manual ? 1 : 3));
    try {
      const matchesRequestedScope = () => !options.scope
        || (projectIdRef.current === options.scope.projectId && activeInspirationIdRef.current === options.scope.inspirationId);
      const shouldApplyToCurrentView = () => matchesRequestedScope() && activeChapterIdRef.current === chapter.id;
      let result: ChapterReviewResult | null = null;
      let lastError: unknown = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          if (attempt > 1 && options.manual) {
            setAiNotice(`第 ${chapter.index} 章评分网络波动，正在重试（${attempt}/${maxAttempts}）`);
          }
          result = await apiRequest<ChapterReviewResult>(`/novel_project_app/chapters/${chapterId}/review`, {
            method: 'POST',
            body: JSON.stringify({ instruction, chapter_content: chapter.content }),
          });
          if (result.review.score == null && result.chapter.score == null) {
            throw new Error('评分接口未返回有效分数');
          }
          break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!result) throw lastError || new Error('章节评分失败');
      const reviewedChapter = toChapter(result.chapter);
      const nextScore = result.review.score ?? result.chapter.score ?? score;
      if (matchesRequestedScope()) {
        setChapters((items) => items.map((item) => (item.id === chapter.id ? reviewedChapter : item)));
        setStageCompletion((items) => ({ ...items, review: true }));
      }
      if (shouldApplyToCurrentView()) {
        setScore(nextScore);
      }
      const tips = [
        ...(result.review.problems || []).map((item) => item.message).filter(Boolean),
        ...(result.review.suggestions || []),
        ...(result.review.continuity_notes || []),
      ];
      if (options.manual && shouldApplyToCurrentView()) {
        setAiNotice(tips[0] || `第 ${reviewedChapter.index} 章评分完成：${nextScore}`);
      }
      if (options.manual) setToast(`第 ${reviewedChapter.index} 章已重新评分`);
      if (shouldApplyToCurrentView()) {
        setActiveChapterId(reviewedChapter.id);
      }
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : options.manual ? '重新评分失败' : '章节评分失败';
      if (options.manual) {
        setAiNotice(message);
        handleAiFailure(message);
        setToast(message);
      }
      return false;
    } finally {
      chapterReviewInFlightRef.current.delete(chapterId);
      setReviewingChapterIds((items) => items.filter((item) => item !== chapterId));
      void loadWorkbenchAssets();
    }
  }

  useEffect(() => {
    if (
      (activeTab !== 'chapters' && activeTab !== 'review')
      || workbenchLoading
      || activeChapterIsGenerating
      || activeChapterIsFinalizing
      || chapterRevisionBusy
      || !activeChapterBackendId
      || !hasGeneratedContent(activeChapter)
      || hasCompletedChapterReview(activeChapter)
      || chapterReviewInFlightRef.current.has(activeChapterBackendId)
    ) return;

    void reviewGeneratedChapter(
      activeChapter,
      '进入尚未评分的章节后后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
      project?.id
        ? { scope: { projectId: project.id, inspirationId: activeInspirationId } }
        : {},
    );
  // 评分函数使用当前页面 ref；只监听评分触发所需的稳定字段，避免状态更新造成重复请求。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeChapter.id,
    activeChapter.backendId,
    activeChapter.content,
    activeChapter.score,
    activeChapter.review,
    activeChapterBackendId,
    activeChapterIsFinalizing,
    activeChapterIsGenerating,
    activeInspirationId,
    activeTab,
    chapterRevisionBusy,
    project?.id,
    workbenchLoading,
  ]);

  useEffect(() => {
    if (activeTab !== 'chapters') {
      firstChapterInitializationAttemptsRef.current.clear();
      return;
    }
    if (
      workbenchLoading
      || chapterPlanGenerating
      || revisionBusy
      || chapterRevisionBusy
      || !project?.id
      || !activeInspirationId
      || !chapters.length
    ) return;

    const firstChapter = [...chapters]
      .sort((a, b) => Number(a.index) - Number(b.index))
      .find((chapter) => Number(chapter.index) === 1) || chapters[0];
    if (!firstChapter?.backendId) return;
    const scopeKey = `${project.id}:${activeInspirationId}`;
    if (hasGeneratedContent(firstChapter)) {
      firstChapterInitializationAttemptsRef.current.delete(scopeKey);
      return;
    }
    if (firstChapterInitializationRef.current.has(scopeKey)) return;
    const previousAttempts = firstChapterInitializationAttemptsRef.current.get(scopeKey) || 0;
    if (previousAttempts >= 3) return;

    const attempt = previousAttempts + 1;
    firstChapterInitializationRef.current.add(scopeKey);
    firstChapterInitializationAttemptsRef.current.set(scopeKey, attempt);
    setRevisionBusy(true);
    setLiveGenerating(true);
    setGeneratingChapterId(firstChapter.id);
    setFinalizingChapterId(null);
    setActiveChapterId(firstChapter.id);
    setAiNotice('正在默认生成第 1 章正文');

    void (async () => {
      try {
        const result = await generateChapterWithStatus({
          targetProjectId: project.id,
          inspirationId: activeInspirationId,
          chapterNo: 1,
          targetWords: chapterTargetWords(firstChapter, project),
          instruction: '进入章节阶段后默认生成第一章正文：严格遵守标题、梗概、设定、人物关系和伏笔，开篇要有明确钩子、冲突和继续阅读动力。',
          onStatus: setAiNotice,
          onChunk: (content, reset = false) => {
            setChapters((items) => items.map((chapter) => (
              Number(chapter.index) === 1
                ? { ...chapter, content: reset ? '' : `${chapter.content || ''}${content}`, words: (reset ? '' : `${chapter.content || ''}${content}`).length }
                : chapter
            )));
          },
          onContentComplete: (message) => {
            setLiveGenerating(false);
            setFinalizingChapterId(firstChapter.id);
            setAiNotice(message);
          },
        });
        if (
          projectIdRef.current !== project.id
          || activeInspirationIdRef.current !== activeInspirationId
        ) return;
        const initializedChapter = toChapter(result.chapter);
        setChapters((items) => items.map((chapter) => (
          Number(chapter.index) === 1 ? initializedChapter : chapter
        )));
        setActiveChapterId(initializedChapter.id);
        setScore(chapterScoreValue(initializedChapter));
        setStageCompletion((items) => ({ ...items, chapters: true }));
        setLastSavedAt(Date.now());
        if (result.chapter.id) {
          void extractChapterMemory(result.chapter.id, '默认生成第一章后抽取后续写作必须记住的事实、人物状态、事件结果和伏笔');
        }
        setRevisionBusy(false);
        setLiveGenerating(false);
        setGeneratingChapterId(null);
        setFinalizingChapterId(null);
        firstChapterInitializationAttemptsRef.current.delete(scopeKey);
        setAiNotice('第 1 章正文已生成并保存，后台正在自动评分');
        setToast('第 1 章正文已生成并保存；后台正在自动评分');
        void reviewGeneratedChapter(
          initializedChapter,
          '默认生成第 1 章并保存后后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
          { scope: { projectId: project.id, inspirationId: activeInspirationId } },
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : '第一章正文初始化失败';
        if (
          projectIdRef.current === project.id
          && activeInspirationIdRef.current === activeInspirationId
        ) {
          if (attempt < 3) {
            setAiNotice(`${message}，系统将自动进行第 ${attempt + 1} 次尝试`);
          } else {
            setAiNotice(message);
            setToast(`${message}，可稍后重新进入章节自动重试`);
            handleAiFailure(message);
          }
          await loadWorkbench(activeInspirationId, project.id);
        }
      } finally {
        firstChapterInitializationRef.current.delete(scopeKey);
        setRevisionBusy(false);
        setLiveGenerating(false);
        setGeneratingChapterId(null);
        setFinalizingChapterId(null);
        void loadWorkbenchAssets();
      }
    })();
  // 生成函数读取当前 ref；把它加入依赖会在流式更新章节时反复重启初始化。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeInspirationId,
    activeTab,
    chapterPlanGenerating,
    chapterRevisionBusy,
    chapters,
    extractChapterMemory,
    handleAiFailure,
    loadWorkbench,
    loadWorkbenchAssets,
    project,
    revisionBusy,
    setToast,
    workbenchLoading,
  ]);

  const fetchAutoGenerateEstimate = async () => {
    if (!project) return fallbackAutoGenerateEstimate;
    const params = new URLSearchParams();
    if (activeInspirationId) params.set('inspiration_id', String(activeInspirationId));
    if (nextChapterToGenerate?.index) params.set('from_chapter_no', String(Number(nextChapterToGenerate.index)));
    const result = await apiRequest<{
      account_balance: number;
      cost_per_chapter: number;
      affordable_chapters: number;
      remaining_chapters: number;
      planned_chapters: number;
      billing_multiplier: number;
      basis: string;
    }>(`/novel_project_app/projects/${project.id}/chapters/auto-generate-estimate?${params.toString()}`);
    const nextEstimate = {
      accountBalance: Number(result.account_balance || 0),
      costPerChapter: Number(result.cost_per_chapter || 0),
      affordableChapters: Number(result.affordable_chapters || 0),
      remainingChapters: Number(result.remaining_chapters || 0),
      plannedChapters: Number(result.planned_chapters || 0),
      billingMultiplier: Number(result.billing_multiplier || 1),
      basis: result.basis || '',
    };
    setAutoGenerateEstimate(nextEstimate);
    setWorkbenchAssets((items) => ({
      ...items,
      account: {
        ...items.account,
        balance: nextEstimate.accountBalance,
      },
    }));
    return nextEstimate;
  };

  const openAutoGenerate = async () => {
    if (!requireLogin()) return;
    if (autoGenerateRunningForCurrent) {
      setAutoGenerateOpen(true);
      return;
    }
    if (autoGenerateState === 'running') {
      setToast('已有作品正在全自动生成，请切回该作品后终止');
      setAiNotice('已有作品正在全自动生成，请先终止后再启动新的队列');
      return;
    }
    if (autoGenerateBlockedByOtherTask) {
      setToast('当前 AI 任务尚未结束，请完成或终止后再启动全自动生成');
      setAiNotice('当前 AI 任务尚未结束，暂不能启动全自动生成');
      return;
    }
    setAutoGenerateOpen(true);
    setAutoGenerateState('checking');
    setAutoGenerateScope(project ? { projectId: project.id, inspirationId: activeInspirationId } : null);
    autoGenerateAbortRef.current = false;
    let estimate = fallbackAutoGenerateEstimate;
    try {
      estimate = await fetchAutoGenerateEstimate();
      setAiNotice('余额评估完成');
    } catch (error) {
      setAutoGenerateEstimate(fallbackAutoGenerateEstimate);
      setAiNotice(error instanceof Error ? error.message : '余额评估失败，已使用兜底估算');
    }
    const planned = autoGenerateQueueCandidates.slice(0, estimate.plannedChapters);
    setAutoGenerateSteps(planned.map((chapter) => ({
      id: chapter.id,
      index: chapter.index,
      title: chapter.title,
      status: 'pending',
      message: '等待生成',
    })));
    if (!autoGenerateAbortRef.current) setAutoGenerateState('ready');
  };

  const stopAutoGenerate = () => {
    autoGenerateAbortRef.current = true;
    autoGenerateRequestRef.current?.abort();
    setAutoGenerateState('stopped');
    setAutoGenerateSteps((items) => items.map((item) => (
      item.status === 'running' || item.status === 'reviewing'
        ? { ...item, status: 'stopped', message: '已终止，已完成内容保留' }
        : item
    )));
    setRevisionBusy(false);
    setLiveGenerating(false);
    setGeneratingChapterId(null);
    setFinalizingChapterId(null);
    setAiNotice('已终止全自动生成');
  };

  const updateAutoGenerateStep = (id: string, status: AutoGenerateStepStatus, message: string) => {
    setAutoGenerateSteps((items) => items.map((item) => (item.id === id ? { ...item, status, message } : item)));
  };

  const generateChapterWithStatus = async ({
    targetProjectId,
    inspirationId,
    chapterNo,
    instruction,
    targetWords,
    signal,
    onStatus,
    onChunk,
    onContentComplete,
  }: {
    targetProjectId: number;
    inspirationId: number | null;
    chapterNo: number;
    instruction: string;
    targetWords: number;
    signal?: AbortSignal;
    onStatus?: (message: string) => void;
    onChunk?: (content: string, reset?: boolean) => void;
    onContentComplete?: (message: string) => void;
  }): Promise<ChapterGenerateResult> => {
    const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${targetProjectId}/chapters/generate-stream`, {
      method: 'POST',
      headers: authHeaders(),
      signal,
      body: JSON.stringify({
        inspiration_id: inspirationId,
        chapter_no: chapterNo,
        instruction,
        target_words: targetWords,
      }),
    });
    if (!response.ok || !response.body) {
      throw new Error(await readResponseError(response, '章节生成失败'));
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: ChapterGenerateResult | null = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.trim()) continue;
        const event = JSON.parse(line);
        if (event.type === 'status' || event.type === 'heartbeat') {
          onStatus?.(event.message || 'AI仍在生成正文，请继续等待');
        }
        if (event.type === 'chunk') onChunk?.(String(event.content || ''));
        if (event.type === 'content_complete') {
          const message = String(event.message || '正文生成完成，正在保存');
          onContentComplete?.(message);
          onStatus?.(message);
        }
        if (event.type === 'reset') {
          onStatus?.(event.message || '网络波动，正在自动重试');
          onChunk?.('', true);
        }
        if (event.type === 'done') result = event.result as ChapterGenerateResult;
        if (event.type === 'error') throw new Error(event.message || '章节生成失败');
      }
    }
    if (!result) throw new Error('章节生成连接已结束，但没有收到完整结果');
    return result;
  };

  const startAutoGenerate = async () => {
    if (!requireLogin()) return;
    if (!project || autoGenerateBlockedByOtherTask) {
      const message = project ? '当前 AI 任务尚未结束，请稍后再启动全自动生成' : '请先登录并同步工作台数据';
      setAiNotice(message);
      setToast(message);
      return;
    }
    const runningScope = { projectId: project.id, inspirationId: activeInspirationId };
    autoGenerateAbortRef.current = false;
    setAutoGenerateScope(runningScope);
    setAutoGenerateState('checking');
    setLiveGenerating(false);
    setAiNotice('正在确认待生成章节和账户余额');
    try {
      let latestEstimate: AutoGenerateEstimate;
      try {
        latestEstimate = await fetchAutoGenerateEstimate();
      } catch (error) {
        latestEstimate = autoGenerateEstimate.plannedChapters > 0
          ? autoGenerateEstimate
          : fallbackAutoGenerateEstimate;
        setAutoGenerateEstimate(latestEstimate);
        setAiNotice(error instanceof Error
          ? `实时余额评估暂时不可用，按当前余额继续：${error.message}`
          : '实时余额评估暂时不可用，按当前余额继续');
      }
      if (autoGenerateAbortRef.current) {
        setAutoGenerateState('stopped');
        return;
      }
      if (latestEstimate.plannedChapters <= 0) {
        setAutoGenerateState('ready');
        setAiNotice(latestEstimate.remainingChapters <= 0 ? '当前没有待生成章节' : '墨水余额不足，无法启动下一章生成');
        setToast(latestEstimate.remainingChapters <= 0 ? '当前没有待生成章节' : '墨水余额不足');
        return;
      }
      const queue = autoGenerateQueueCandidates.slice(0, latestEstimate.plannedChapters);
      setAutoGenerateSteps(queue.map((chapter) => ({
        id: chapter.id,
        index: chapter.index,
        title: chapter.title,
        status: 'pending',
        message: '等待生成',
      })));
      if (!queue.length) {
        setAutoGenerateState('done');
        setAiNotice('当前没有待生成章节');
        window.setTimeout(() => setAutoGenerateOpen(false), 1200);
        return;
      }
      setAutoGenerateState('running');
      setRevisionBusy(true);
      setAiNotice('AI自动生成队列已启动');
      for (const chapter of queue) {
        if (autoGenerateAbortRef.current) break;
        setGeneratingChapterId(chapter.id);
        setFinalizingChapterId(null);
        setLiveGenerating(true);
        const stillViewingScope = projectIdRef.current === runningScope.projectId
          && activeInspirationIdRef.current === runningScope.inspirationId;
        if (stillViewingScope) {
          setActiveChapterId(chapter.id);
          activeChapterIdRef.current = chapter.id;
          setChapters((items) => items.map((item) => (
            item.id === chapter.id ? { ...item, status: 'active', progress: 0 } : item
          )));
        }
        setAiNotice(`AI正在自动生成第 ${chapter.index} 章`);
        updateAutoGenerateStep(chapter.id, 'running', '正在生成正文并写入章节');
        autoGenerateRequestRef.current = new AbortController();
        let streamedContent = '';
        let contentStreamCompleted = false;
        try {
          const result = await generateChapterWithStatus({
            targetProjectId: project.id,
            inspirationId: activeInspirationId,
            chapterNo: Number(chapter.index),
            instruction: '全自动生成队列：只能按章节顺序连续写作。必须承接上一章正文结尾的时间、地点、人物状态、行动结果和未决悬念，再推进当前章纲；严格使用人物档案中的姓名、身份、关系与能力，不得换人、串书、重启剧情或写出章纲范围。生成成功后保存当前章，再继续下一章。',
            targetWords: chapterTargetWords(chapter, project),
            signal: autoGenerateRequestRef.current.signal,
            onStatus: (message) => {
              setAiNotice(message);
              updateAutoGenerateStep(chapter.id, 'running', message);
            },
            onChunk: (content, reset = false) => {
              streamedContent = reset ? '' : `${streamedContent}${content}`;
              if (
                projectIdRef.current !== runningScope.projectId
                || activeInspirationIdRef.current !== runningScope.inspirationId
              ) return;
              const targetWords = chapterTargetWords(chapter, project);
              setChapters((items) => items.map((item) => (
                item.id === chapter.id
                  ? {
                    ...item,
                    content: streamedContent,
                    words: streamedContent.length,
                    progress: Math.min(99, Math.round((streamedContent.length / targetWords) * 100)),
                    status: 'active',
                  }
                  : item
              )));
            },
            onContentComplete: (message) => {
              contentStreamCompleted = true;
              setLiveGenerating(false);
              setFinalizingChapterId(chapter.id);
              setAiNotice(message);
              updateAutoGenerateStep(chapter.id, 'running', message);
            },
          });
          const nextChapter = toChapter(result.chapter);
          const stillViewingScope = projectIdRef.current === runningScope.projectId
            && activeInspirationIdRef.current === runningScope.inspirationId;
          if (stillViewingScope) {
            setChapters((items) => {
              const generatedNo = Number(nextChapter.index);
              const updated = items.map((item) => {
                if (item.id === chapter.id) return nextChapter;
                if (Number(item.index) > generatedNo && hasGeneratedContent(item)) return { ...item, status: 'stale' as const };
                return item;
              });
              setStageCompletion((current) => ({ ...current, chapters: updated.some(hasGeneratedContent) }));
              return updated;
            });
            setActiveChapterId(nextChapter.id);
          }
          setLastSavedAt(Date.now());
          setLiveGenerating(false);
          setGeneratingChapterId(null);
          setFinalizingChapterId(null);
          updateAutoGenerateStep(chapter.id, 'running', `正文已保存，${nextChapter.words.toLocaleString()} 字，正在整理章节记忆`);
          const memoryPromise = result.chapter.id
            ? extractChapterMemory(result.chapter.id, '全自动生成后抽取事实、人物状态和伏笔')
            : Promise.resolve();
          await memoryPromise;
          setLastSavedAt(Date.now());
          updateAutoGenerateStep(chapter.id, 'done', `正文已保存，${nextChapter.words.toLocaleString()} 字；后台自动评分已启动`);
          void reviewGeneratedChapter(
            nextChapter,
            '全自动生成章节保存后后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
            { scope: runningScope },
          );
        } catch (error) {
          if (autoGenerateAbortRef.current) {
            updateAutoGenerateStep(chapter.id, 'stopped', '已终止，已完成内容保留');
            break;
          }
          const originalMessage = error instanceof Error ? error.message : '自动生成失败';
          let recoveredChapter: Chapter | null = null;
          let recoveryMessage = '';
          try {
            // 流式连接可能在后端保存成功后断开。先以服务器快照为准，避免重复保存。
            const snapshot = await apiRequest<WorkbenchSnapshot>(
              workbenchPath(runningScope.projectId, runningScope.inspirationId),
            );
            const persisted = snapshot.chapters.find((item) => item.chapter_no === Number(chapter.index));
            if (persisted && hasGeneratedContent(toChapter(persisted))) {
              recoveredChapter = toChapter(persisted);
              recoveryMessage = '生成连接异常，但正文已在服务器保存';
            } else if (contentStreamCompleted && streamedContent.trim() && chapter.backendId) {
              // 正文流明确结束、但后置校验或响应收尾失败时，把用户已经看到的完整正文
              // 补保存为草稿。未收到 content_complete 的半截正文绝不能走这条路径。
              updateAutoGenerateStep(chapter.id, 'running', '正文已生成，正在补保存草稿');
              const saved = await apiRequest<BackendChapter>(`/novel_project_app/chapters/${chapter.backendId}`, {
                method: 'PATCH',
                body: JSON.stringify({
                  title: chapter.title,
                  summary: chapter.summary,
                  content: streamedContent,
                  status: 'drafting',
                  target_words: chapterTargetWords(chapter, project),
                  score: null,
                  review: {},
                }),
              });
              recoveredChapter = toChapter(saved);
              recoveryMessage = '后置处理异常，已自动补保存正文';
            }
          } catch (recoveryError) {
            recoveryMessage = recoveryError instanceof Error ? recoveryError.message : '正文补保存失败';
          }
          if (recoveredChapter) {
            const completedChapter = recoveredChapter;
            if (
              projectIdRef.current === runningScope.projectId
              && activeInspirationIdRef.current === runningScope.inspirationId
            ) {
              setChapters((items) => items.map((item) => (
                item.id === chapter.id ? completedChapter : item
              )));
              setActiveChapterId(completedChapter.id);
              activeChapterIdRef.current = completedChapter.id;
              setStageCompletion((current) => ({ ...current, chapters: true }));
            }
            setLastSavedAt(Date.now());
            setLiveGenerating(false);
            setGeneratingChapterId(null);
            setFinalizingChapterId(null);
            updateAutoGenerateStep(
              chapter.id,
              'done',
              `${recoveryMessage}，${completedChapter.words.toLocaleString()} 字`,
            );
            if (completedChapter.backendId) {
              void extractChapterMemory(completedChapter.backendId, '全自动生成补保存后抽取事实、人物状态和伏笔');
            }
            continue;
          }
          // 没有确认正文落库时立即恢复服务器状态，不能让内存里的流式正文把下一章解锁。
          if (
            projectIdRef.current === runningScope.projectId
            && activeInspirationIdRef.current === runningScope.inspirationId
          ) {
            try {
              const snapshot = await apiRequest<WorkbenchSnapshot>(
                workbenchPath(runningScope.projectId, runningScope.inspirationId),
              );
              const synced = snapshot.chapters.map((item, index) => toChapter(item, index + 1));
              setChapters(synced);
              const failedChapter = synced.find((item) => Number(item.index) === Number(chapter.index));
              if (failedChapter) {
                setActiveChapterId(failedChapter.id);
                activeChapterIdRef.current = failedChapter.id;
              }
            } catch {
              setChapters((items) => items.map((item) => (
                item.id === chapter.id
                  ? { ...item, content: '', words: 0, progress: 0, status: 'todo' as const }
                  : item
              )));
            }
          }
          const message = recoveryMessage
            ? `${originalMessage}；正文未保存：${recoveryMessage}`
            : `${originalMessage}；正文未保存，请重新生成当前章`;
          updateAutoGenerateStep(chapter.id, 'failed', message);
          throw new Error(message);
        }
      }
      if (autoGenerateAbortRef.current) {
        setAutoGenerateState('stopped');
        setAiNotice('全自动生成已终止，已完成的章节已保留');
      } else {
        setAutoGenerateState('done');
        setToast(`已完成全自动生成 ${queue.length} 章`);
        setAiNotice(`自动生成完成 ${queue.length} 章`);
        window.setTimeout(() => {
          if (!autoGenerateAbortRef.current) setAutoGenerateOpen(false);
        }, 1400);
      }
    } catch (error) {
      if (autoGenerateAbortRef.current) {
        setAutoGenerateState('stopped');
        setAiNotice('全自动生成已终止');
      } else {
        setAutoGenerateState('ready');
        const message = error instanceof Error ? error.message : '自动生成失败';
        setAiNotice(message);
        handleAiFailure(message);
      }
    } finally {
      autoGenerateRequestRef.current = null;
      setRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      void loadWorkbenchAssets();
    }
  };

  const reviseActiveChapter = async () => {
    if (!requireLogin()) return;
    const instruction = revisionPrompt.trim();
    if (!instruction) {
      setChapterRevisionNotice('请先写清楚希望微调哪里，比如“把开头压迫感加强一点”。');
      return;
    }
    if (!project) {
      setChapterRevisionNotice('请先登录并同步工作台数据。');
      return;
    }
    if (!activeChapter.backendId || !activeChapter.content?.trim()) {
      setChapterRevisionNotice('当前章节还没有正文，无法微调。请先生成章节正文。');
      return;
    }
    if (instruction.length < 4 || /^(你好|您好|hi|hello|在吗|谢谢|test|测试)$/i.test(instruction)) {
      setChapterRevisionNotice('这个内容不像章节修改意见。这里仅支持对当前章节正文做小幅微调，例如节奏、氛围、对话、伏笔或局部表达。');
      return;
    }
    setChapterRevisionBusy(true);
    setGeneratingChapterId(activeChapter.id);
    setFinalizingChapterId(null);
    setLiveGenerating(true);
    setChapterRevisionNotice('AI 正在判断修改意见，并准备微调当前章节。');
    setAiNotice(`AI正在微调第 ${activeChapter.index} 章`);
    try {
      // 编辑器正文可能刚由流式生成或用户手动调整，先保存用户眼前这一版，
      // 避免微调接口从对象存储读到旧内容或空内容。
      await apiRequest<BackendChapter>(`/novel_project_app/chapters/${activeChapter.backendId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: activeChapter.title,
          summary: activeChapter.summary,
          content: activeChapter.content,
          status: 'drafting',
          target_words: activeChapterTargetWords,
        }),
      });
      const result = await apiRequest<ChapterReviseResult>(`/novel_project_app/chapters/${activeChapter.backendId}/revise`, {
        method: 'POST',
        body: JSON.stringify({ instruction, chapter_content: activeChapter.content }),
      });
      if (!result.applied || !result.chapter) {
        const message = result.message || '这个意见不适合用于当前章节微调。';
        setChapterRevisionNotice(message);
        setAiNotice(message);
        return;
      }
      const revisedChapter = toChapter(result.chapter);
      setChapters((items) => items.map((chapter) => (chapter.id === activeChapter.id ? revisedChapter : chapter)));
      setActiveChapterId(revisedChapter.id);
      setRevisionPrompt('');
      setLastSavedAt(Date.now());
      setChapterRevisionNotice(result.message || '已按意见微调当前章节。');
      setAiNotice('章节已微调并保存');
      void extractChapterMemory(result.chapter.id, '章节微调后重新抽取事实、人物状态、事件结果和伏笔');
      setChapterRevisionNotice('当前章节已微调完成；后台正在按新正文自动重新评分。');
      setToast('章节已微调并保存；后台正在自动重新评分');
      void reviewGeneratedChapter(
        revisedChapter,
        '章节微调并保存后后台自动重新评分：检查修改效果、连续性、节奏、伏笔和人设稳定性。',
        { scope: { projectId: project.id, inspirationId: activeInspirationId } },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '章节微调失败';
      setChapterRevisionNotice(message);
      setAiNotice(message);
      handleAiFailure(message);
    } finally {
      setChapterRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      void loadWorkbenchAssets();
    }
  };

  const generateNext = async () => {
    if (!requireLogin()) return;
    const next = nextChapterToGenerate;
    if (!next) {
      setToast('所有章节都已生成');
      setAiNotice('当前没有待生成章节');
      return;
    }
    if (!project || revisionBusy) {
      setAiNotice(project ? 'AI正在处理中' : '请先登录并同步工作台数据');
      return;
    }
    const generationScope = {
      projectId: project.id,
      inspirationId: activeInspirationId,
    };
    setActiveChapterId(next.id);
    switchTab('chapters');
    setGeneratingChapterId(next.id);
    setFinalizingChapterId(null);
    setLiveGenerating(true);
    setToast(`正在生成第 ${next.index} 章`);
    setRevisionBusy(true);
    setAiNotice(`AI正在生成第 ${next.index} 章`);
    let streamedContent = '';
    let contentStreamCompleted = false;
    try {
      const result = await generateChapterWithStatus({
        targetProjectId: project.id,
        inspirationId: activeInspirationId,
        chapterNo: Number(next.index),
        instruction: '严格按章节顺序生成。开头必须直接承接上一章正文结尾的时间、地点、在场人物、行动结果和未决悬念，再完成当前章纲；严格使用人物档案中的姓名、身份、关系、能力和当前状态，不得换人、串书、重复上一章事件、擅自跳时空或写出章纲范围。',
        targetWords: chapterTargetWords(next, project),
        onStatus: setAiNotice,
        onChunk: (content, reset = false) => {
          streamedContent = reset ? '' : `${streamedContent}${content}`;
          setChapters((items) => items.map((chapter) => (
            chapter.id === next.id
              ? { ...chapter, content: streamedContent, words: streamedContent.length, status: 'active' }
              : chapter
          )));
        },
        onContentComplete: (message) => {
          contentStreamCompleted = true;
          setLiveGenerating(false);
          setFinalizingChapterId(next.id);
          setAiNotice(message);
        },
      });
      const nextChapter = toChapter(result.chapter);
      setChapters((items) => {
        const generatedNo = Number(nextChapter.index);
        const updated = items.map((chapter) => {
          if (chapter.id === next.id) return nextChapter;
          if (Number(chapter.index) > generatedNo && hasGeneratedContent(chapter)) return { ...chapter, status: 'stale' as const };
          return chapter;
        });
        setStageCompletion((current) => ({ ...current, chapters: updated.some(hasGeneratedContent) }));
        return updated;
      });
      setActiveChapterId(nextChapter.id);
      setAiNotice(`第 ${nextChapter.index} 章已生成`);
      if (result.chapter.id) {
        void extractChapterMemory(result.chapter.id, '从新章节中整理事实、人物状态、事件结果和伏笔');
      }
      setRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      setToast(`第 ${nextChapter.index} 章正文已生成并保存；后台正在自动评分`);
      void reviewGeneratedChapter(
        nextChapter,
        '章节生成并保存后后台自动评分：检查连续性、节奏、爽点、伏笔和人设稳定性。',
        { scope: generationScope },
      );
    } catch (error) {
      const originalMessage = error instanceof Error ? error.message : '章节生成失败';
      let serverSnapshot: WorkbenchSnapshot | null = null;
      try {
        serverSnapshot = await apiRequest<WorkbenchSnapshot>(
          workbenchPath(generationScope.projectId, generationScope.inspirationId),
        );
        const persisted = serverSnapshot.chapters.find((chapter) => chapter.chapter_no === Number(next.index));
        const persistedChapter = persisted ? toChapter(persisted) : null;
        if (persistedChapter && hasGeneratedContent(persistedChapter)) {
          if (
            projectIdRef.current === generationScope.projectId
            && activeInspirationIdRef.current === generationScope.inspirationId
          ) {
            setChapters(serverSnapshot.chapters.map((chapter, index) => toChapter(chapter, index + 1)));
            setActiveChapterId(persistedChapter.id);
            activeChapterIdRef.current = persistedChapter.id;
            setStageCompletion((items) => ({ ...items, chapters: true }));
          }
          setAiNotice(`第 ${persistedChapter.index} 章正文已保存，后置处理暂未完成`);
          setToast(`第 ${persistedChapter.index} 章正文已生成并保存，后置处理暂未完成，可稍后重试`);
          return;
        }
      } catch {
        // 恢复查询失败时保留原始生成错误，避免掩盖真正的失败原因。
      }
      if (contentStreamCompleted && streamedContent.trim() && next.backendId) {
        try {
          setAiNotice(`第 ${next.index} 章正文已生成，正在补保存草稿`);
          const saved = await apiRequest<BackendChapter>(`/novel_project_app/chapters/${next.backendId}`, {
            method: 'PATCH',
            body: JSON.stringify({
              title: next.title,
              summary: next.summary,
              content: streamedContent,
              status: 'drafting',
              target_words: chapterTargetWords(next, project),
              score: null,
              review: {},
            }),
          });
          const recoveredChapter = toChapter(saved);
          setChapters((items) => items.map((item) => (item.id === next.id ? recoveredChapter : item)));
          setActiveChapterId(recoveredChapter.id);
          activeChapterIdRef.current = recoveredChapter.id;
          setStageCompletion((items) => ({ ...items, chapters: true }));
          setLastSavedAt(Date.now());
          setAiNotice(`第 ${recoveredChapter.index} 章正文已补保存，后置处理可稍后重试`);
          setToast(`第 ${recoveredChapter.index} 章正文已生成并保存`);
          if (recoveredChapter.backendId) {
            void extractChapterMemory(recoveredChapter.backendId, '章节补保存后整理事实、人物状态和伏笔');
          }
          return;
        } catch (recoveryError) {
          const recoveryMessage = recoveryError instanceof Error ? recoveryError.message : '补保存失败';
          const synced = serverSnapshot?.chapters.map((item, index) => toChapter(item, index + 1));
          if (synced?.length) {
            setChapters(synced);
            const failedChapter = synced.find((item) => Number(item.index) === Number(next.index));
            if (failedChapter) {
              setActiveChapterId(failedChapter.id);
              activeChapterIdRef.current = failedChapter.id;
            }
          } else {
            setChapters((items) => items.map((item) => (
              item.id === next.id
                ? { ...item, content: '', words: 0, progress: 0, status: 'todo' as const }
                : item
            )));
          }
          const message = `${originalMessage}；正文补保存失败：${recoveryMessage}`;
          setAiNotice(message);
          setToast('正文没有保存成功，已停留在当前章，请重新生成');
          return;
        }
      }
      const synced = serverSnapshot?.chapters.map((item, index) => toChapter(item, index + 1));
      if (synced?.length) {
        setChapters(synced);
        const failedChapter = synced.find((item) => Number(item.index) === Number(next.index));
        if (failedChapter) {
          setActiveChapterId(failedChapter.id);
          activeChapterIdRef.current = failedChapter.id;
        }
      } else {
        setChapters((items) => items.map((item) => (
          item.id === next.id
            ? { ...item, content: '', words: 0, progress: 0, status: 'todo' as const }
            : item
        )));
      }
      setAiNotice(originalMessage);
      setToast('正文没有保存成功，已停留在当前章，请重新生成');
    } finally {
      setRevisionBusy(false);
      setLiveGenerating(false);
      setGeneratingChapterId(null);
      setFinalizingChapterId(null);
      void loadWorkbenchAssets();
    }
  };

  const recalcScore = async () => {
    if (!requireLogin()) return;
    if (!activeChapter.backendId || revisionBusy || activeChapterIsReviewing) {
      setAiNotice(activeChapter.backendId ? 'AI正在处理中' : '当前章节尚未同步到后端');
      return;
    }
    await reviewGeneratedChapter(activeChapter, '手动重新评分：重点检查是否跑题、人物是否崩、伏笔是否冲突。', { manual: true });
  };

  const selectChapter = (chapter: Chapter) => {
    setActiveChapterId(chapter.id);
    activeChapterIdRef.current = chapter.id;
    setScore(chapterScoreValue(chapter));
    setToast(`已切换到第 ${chapter.index} 章`);
  };

  const dragAiPanel = (event: React.PointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button, input, textarea, select, .ai-resize-handle')) return;
    event.preventDefault();

    const startX = event.clientX;
    const startY = event.clientY;
    const startLayout = aiPanelLayout;

    const onPointerMove = (moveEvent: PointerEvent) => {
      setAiPanelLayout(
        clampAiPanelLayout({
          ...startLayout,
          x: startLayout.x + moveEvent.clientX - startX,
          y: startLayout.y + moveEvent.clientY - startY,
        }),
      );
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const resizeAiPanel = (event: React.PointerEvent<HTMLSpanElement>, direction: string) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startLayout = aiPanelLayout;

    const onPointerMove = (moveEvent: PointerEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      let nextLayout = { ...startLayout };

      if (direction.includes('e')) nextLayout.width = startLayout.width + deltaX;
      if (direction.includes('s')) nextLayout.height = startLayout.height + deltaY;
      if (direction.includes('w')) {
        nextLayout.x = startLayout.x + deltaX;
        nextLayout.width = startLayout.width - deltaX;
      }
      if (direction.includes('n')) {
        nextLayout.y = startLayout.y + deltaY;
        nextLayout.height = startLayout.height - deltaY;
      }

      nextLayout = clampAiPanelLayout(nextLayout);
      setAiPanelLayout(nextLayout);
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const aiSceneConfig = useMemo(() => {
    const configs: Record<TopTabKey, { tipTitle: string; tipBody: string; prompts: string[]; placeholder: string }> = {
      inspiration: {
        tipTitle: '检测到您当前正在创作灵感',
        tipBody: '输入一句话灵感，确认梗概没有问题后，点击“保存并进入设定”就可以继续下一步。',
        prompts: [
          '梗概写得太笼统了，请把关键事件、转折和结尾悬念写得更细。',
          '核心冲突不够强，请强化主角欲望和必须付出的代价。',
          '读者钩子还不够抓人，请改得更适合网文追读。',
          '禁写方向太宽泛，请改成更具体的创作边界。',
        ],
        placeholder: '询问 AI 当前灵感梗概怎么优化',
      },
      settings: {
        tipTitle: '检测到您当前正在完善设定',
        tipBody: '可以让 AI 检查题材、节奏、边界和章节目标是否足够清晰。',
        prompts: [
          '请检查当前设定是否足够支撑后续大纲。',
          '请把禁写方向改得更具体，避免后续跑偏。',
          '请根据当前题材补充更适合网文连载的节奏规则。',
        ],
        placeholder: '询问 AI 当前设定怎么优化',
      },
      characters: {
        tipTitle: '检测到您当前正在设计人物',
        tipBody: '可以让 AI 检查人物目标、秘密、关系和成长线是否互相支撑。',
        prompts: [
          '请检查主角目标和人物弧光是否足够明确。',
          '请补强人物之间的隐藏关系和冲突点。',
          '请找出人物设定里容易写崩的地方。',
        ],
        placeholder: '询问 AI 当前人物设定怎么优化',
      },
      outline: {
        tipTitle: '检测到您当前正在搭建大纲',
        tipBody: '可以让 AI 检查转折、伏笔回收、章节目标和主线推进是否连贯。',
        prompts: [
          '请检查大纲转折是否够强，哪些章节需要加冲突。',
          '请梳理当前伏笔的埋设和回收是否合理。',
          '请找出大纲里可能跑偏或断线的地方。',
        ],
        placeholder: '询问 AI 当前大纲怎么优化',
      },
      chapters: {
        tipTitle: '检测到您当前正在编辑章节',
        tipBody: `可以让 AI 围绕《${activeChapter.title}》分析节奏、爽点、伏笔和章末钩子，并给出可复制的修改建议。`,
        prompts: [
          '请把本章中段节奏改得更紧凑。',
          '请补一个更强的章末钩子。',
          '请检查本章人物行动是否符合设定。',
        ],
        placeholder: '询问 AI 当前章节需要怎么改',
      },
      review: {
        tipTitle: '检测到您当前正在审稿',
        tipBody: '可以让 AI 聚焦问题清单，优先给出可执行的修复方案。',
        prompts: [
          '请把当前问题按严重程度排序并给出修复方案。',
          '请生成一版可以直接应用的修复建议。',
          '请检查这些问题是否会影响后续连载。',
        ],
        placeholder: '询问 AI 当前审稿问题怎么处理',
      },
      publish: {
        tipTitle: '检测到您当前正在准备发布',
        tipBody: '可以让 AI 优化简介、卖点、平台适配和发布资料。',
        prompts: [
          '请把作品简介改得更适合读者点击。',
          '请提炼这本书的核心卖点。',
          '请检查发布资料是否缺少关键信息。',
        ],
        placeholder: '询问 AI 发布资料怎么优化',
      },
    };
    return configs[activeTab];
  }, [activeChapter.title, activeTab]);

  const aiPresetPrompts = aiSceneConfig.prompts;
  const activeAiSession = aiSessions.find((session) => session.id === activeAiSessionId) || aiSessions[0];
  const activeAiMessageCount = activeAiSession?.messages.length || 0;
  const activeAiLastMessage = activeAiSession?.messages[activeAiMessageCount - 1]?.text || '';

  useEffect(() => {
    if (!aiDrawerOpen) return;
    scrollAiChatToBottom(activeAiMessageCount <= 1 ? 'auto' : 'smooth');
  }, [activeAiMessageCount, activeAiLastMessage, activeAiSessionId, aiDrawerOpen, assistantBusy, scrollAiChatToBottom]);

  return (
    <div className="workbench">
      {messageContextHolder}
      <header className="wb-titlebar">
        <button className="wb-home-button" type="button" onClick={onHome}>
          <ArrowLeft size={15} />
          返回首页
        </button>
        <div className="document-title" ref={projectSwitcherRef}>
          <button
            className="document-switch-trigger"
            type="button"
            onClick={() => setProjectSwitcherOpen((open) => !open)}
            disabled={!workbenchInspirations.length || workbenchLoading}
            title="切换当前工作台作品"
          >
            <strong>{documentName}</strong>
            <ChevronDown size={14} />
          </button>
          {projectSwitcherOpen && (
            <div className="document-switch-menu">
              {workbenchInspirations.length ? workbenchInspirations.map((item) => (
                <button
                  className={Number(item.id) === Number(activeInspirationId) ? 'active' : ''}
                  key={item.id}
                  type="button"
                  onClick={() => switchWorkbenchInspiration(item.id)}
                >
                  <BookOpen size={14} />
                  <span>{item.title || `作品 ${item.id}`}</span>
                  {Number(item.id) === Number(activeInspirationId) && <em>当前</em>}
                </button>
              )) : (
                <div className="document-switch-empty">暂无可切换作品</div>
              )}
            </div>
          )}
        </div>
        <div className="save-status">{workbenchLoading ? '正在同步工作台' : saveStatusText}</div>
        {currentUser && (
          <button
            className="workbench-ink-balance"
            type="button"
            title={assetsError || `可用墨水 ${formatInkBalance(workbenchAssets.account.balance)}，冻结 ${formatInkBalance(workbenchAssets.account.frozen)}`}
            onClick={() => { window.location.hash = '#account'; }}
          >
            <Coins size={15} />
            <span>{assetsLoading ? '同步中' : formatInkBalance(workbenchAssets.account.balance)}</span>
            <em>墨水</em>
          </button>
        )}
        <button className="workbench-theme-toggle" type="button" aria-label={theme === 'dark' ? '切换浅色主题' : '切换深色主题'} onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}>
          {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        <div className="workbench-account">
          {currentUser ? (
            <UserAccountMenu
              className="workbench-user-menu"
              user={currentUser}
              setUser={setCurrentUser}
              onAccount={() => { window.location.hash = '#account'; }}
              onOrders={() => { window.location.hash = '#orders'; }}
              onProfile={() => { window.location.hash = '#profile'; }}
              onLogoutComplete={() => {
                setProject(null);
                window.location.hash = '#login';
              }}
            />
          ) : (
            <div className="account-actions">
              <button type="button" onClick={goLogin}>
                <LogIn size={15} />
                登录
              </button>
              <button type="button" onClick={goRegister}>
                <UserCircle size={15} />
                注册
              </button>
            </div>
          )}
        </div>
      </header>

      <nav className="wb-progress" aria-label="创作流程">
        {topTabs.map((tab) => {
          const done = Boolean(stageCompletion[tab.key]);
          const blockingStage = blockingStageFor(tab.key, stageCompletion);
          const locked = Boolean(blockingStage);
          const className = [
            activeTab === tab.key ? 'active' : '',
            done ? 'done' : 'pending',
            locked ? 'locked' : '',
          ].filter(Boolean).join(' ');
          return (
            <a
              className={className}
              href={`#workbench/${tab.key}`}
              key={tab.key}
              aria-disabled={locked}
              title={locked ? `请先完成${blockingStage?.label}` : done ? `${tab.label}已保存` : `${tab.label}暂无已保存内容`}
              onClick={(event) => {
                event.preventDefault();
                switchTab(tab.key);
              }}
            >
              <span>{tab.label}</span>
              {done && <Check className="stage-done-icon" size={14} strokeWidth={3} />}
            </a>
          );
        })}
        <button
          className={aiDrawerOpen ? 'progress-ai-toggle active' : 'progress-ai-toggle'}
          type="button"
          onClick={() => setAiDrawerOpen((open) => !open)}
        >
          <Bot size={15} />
          AI助手
        </button>
      </nav>

      {activeTab === 'chapters' ? (
      <div className="wb-body">
        <aside className="wb-sidebar">
          <div className="project-heading">
            <BookOpen size={18} />
            <strong>{visibleProject.title}</strong>
          </div>

          <section className="side-section chapters">
            <h3>章节 <span>{generatedChapterCount} / {plannedChapterCount}</span></h3>
            {workbenchLoading && !chapters.length ? (
              <div className="chapter-empty-side loading"><Loader2 size={16} />正在加载章节目录...</div>
            ) : chapters.length ? chapters.map((chapter) => (
              <button
                className={chapter.id === activeChapter.id ? 'active' : ''}
                key={chapter.id}
                type="button"
                onClick={() => selectChapter(chapter)}
              >
                {generatingChapterId === chapter.id ? <span className="active-dot" /> : getStatusIcon(chapter)}
                <span>{chapter.index} · {chapter.title}</span>
                {chapter.status === 'stale' && <em>待衔接</em>}
                {(generatingChapterId === chapter.id || isChapterInProgress(chapter)) && <em>{chapter.progress}%</em>}
              </button>
            )) : (
              <div className="chapter-empty-side">暂无章节计划</div>
            )}
          </section>

          <div className="side-footer-action">
            <AppendChaptersButton
              compact
              projectId={project?.id}
              inspirationId={activeInspirationId}
              currentCount={chapters.length}
              maxChapters={maxChapters}
              disabled={autoGenerateRunningForCurrent || autoGenerateBlockedByOtherTask || !chapters.length}
              onMessage={setToast}
              onComplete={async () => {
                await loadWorkbench(activeInspirationId, project?.id);
              }}
            />
            <button className={autoGenerateRunningForCurrent ? 'auto-generate stop' : 'auto-generate'} type="button" onClick={autoGenerateRunningForCurrent ? stopAutoGenerate : openAutoGenerate}>
              {autoGenerateRunningForCurrent ? <AlertTriangle size={18} /> : <Zap size={18} />}
              {autoGenerateRunningForCurrent ? '立即终止' : '全自动生成'}
            </button>
          </div>
        </aside>

        <main className="wb-editor">
          <div className="crumb-row">
            <span>章节</span>
            <span>/</span>
            <span>第 {activeChapter.index} 章</span>
            <span>/</span>
            <strong>{activeChapter.title}</strong>
            <span className={`live ${chapterGenerationIndicator.className}`} role="status" aria-live="polite">
              <i />
              {chapterGenerationIndicator.text}
            </span>
          </div>

          <div className="chapter-title-row">
            <span>CH · {activeChapter.index}</span>
            <h1>{activeChapter.title}</h1>
          </div>

          <section className={activeChapterIsGenerating ? 'editor-card streaming' : 'editor-card'}>
            <div className="editor-meta">
              <span>{activeChapter.words.toLocaleString()} 字 / 目标 {activeChapterTargetWords.toLocaleString()}</span>
              <span>{narrativePerspective}</span>
              <span>{visibleProject.genre}</span>
            </div>
            <div className="chapter-stream-editor">
              <textarea ref={chapterEditorRef} value={activeChapter.content} onChange={(event) => updateChapterContent(event.target.value)} />
              {activeChapterIsGenerating && !activeChapter.content && (
                <div className="chapter-stream-preparing" role="status" aria-live="polite">
                  <Loader2 size={16} />
                  <span>{aiNotice || 'AI 正在分析大纲并准备生成正文...'}</span>
                </div>
              )}
              {activeChapterIsGenerating && <i className="chapter-stream-caret" aria-hidden="true" />}
            </div>
            {(((chapterRevisionBusy) && !autoGenerateRunningForCurrent) || chapterPlanGenerating) && (
              <div className="stage-generating-overlay" aria-live="polite">
                <div className="stage-generating-card">
                  <Loader2 size={30} />
                  <strong>{chapterPlanGenerating ? 'AI 正在生成章节计划' : 'AI 正在微调当前章节'}</strong>
                  <p>{chapterPlanGenerating ? chapterPlanMessage || '正在按大纲创建章节列表，请稍等。' : chapterRevisionBusy ? '正在根据您的修改意见小幅调整正文，并保持原有剧情和设定稳定。' : aiNotice || '正在保存或生成章节内容，请稍等，当前区域暂时不可操作。'}</p>
                  <span><i /></span>
                </div>
              </div>
            )}
          </section>

          {workbenchLoading && !chapters.length && (
            <section className="stage-decision-card chapter-loading-card" aria-live="polite">
              <div className="stage-decision-dialog">
                <Loader2 size={28} />
                <strong>正在加载章节</strong>
                <p>正在同步完整章节目录和第一章内容，请稍等。</p>
              </div>
            </section>
          )}
          {!workbenchLoading && !chapters.length && !chapterPlanGenerating && !chapterPlanPromptDismissed && (
            <section className="stage-decision-card chapter-decision-card">
              <div className="stage-decision-dialog">
                <strong>{chapterPlanAvailable ? '当前还没有章节计划' : '还没有大纲，暂时不能生成章节计划'}</strong>
                <p>{chapterPlanAvailable ? '检测到当前作品还没有章节列表，可以选择手动先看空白编辑器，也可以让 AI 根据大纲生成章节计划。' : '请先回到大纲页完成或生成大纲，然后再进入章节编辑。'}</p>
                <div className="stage-decision-actions">
                  {chapterPlanAvailable ? (
                    <>
                      <button type="button" onClick={() => setChapterPlanPromptDismissed(true)}>手动填写</button>
                      <button className="primary" type="button" onClick={planChaptersFromWorkbench}>AI生成</button>
                    </>
                  ) : (
                    <button className="primary" type="button" onClick={() => switchTab('outline', true)}>返回大纲</button>
                  )}
                </div>
              </div>
            </section>
          )}
          {!workbenchLoading && chapterPlanMessage && !chapterPlanGenerating && !chapters.length && (
            <div className="stage-error-card">
              <strong>章节计划提示</strong>
              <p>{chapterPlanMessage}</p>
              {chapterPlanAvailable && <button type="button" onClick={planChaptersFromWorkbench}>重新生成章节计划</button>}
            </div>
          )}

          <div className="ai-revision-bar">
            <label className="ai-revision-input">
              <Bot size={16} />
              <input
                value={revisionPrompt}
                placeholder="对本章不满意？告诉 AI 需要怎么改"
                onChange={(event) => setRevisionPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    reviseActiveChapter();
                  }
                }}
              />
            </label>
            <button type="button" disabled={!revisionPrompt.trim() || chapterRevisionBusy || revisionBusy || !project} onClick={reviseActiveChapter}>
              {chapterRevisionBusy ? <Loader2 size={17} /> : <Wand2 size={17} />}
              {chapterRevisionBusy ? '修改中' : '修改'}
            </button>
            <button className="generate-next" type="button" disabled={revisionBusy || chapterRevisionBusy || !project} onClick={generateNext}>
              <ArrowRight size={18} />
              {activeChapterIsFinalizing
                ? '校验保存中'
                : activeChapterIsGenerating
                  ? '生成中'
                  : nextChapterToGenerate
                    ? `生成第 ${nextChapterToGenerate.index} 章`
                    : '全部已生成'}
            </button>
            <button
              className="save-review"
              type="button"
              disabled={chapterSaveBusy || chapterRevisionBusy || activeChapterIsGenerating || activeChapterIsFinalizing || !hasGeneratedContent(activeChapter)}
              onClick={saveChapterAndOpenReview}
            >
              {chapterSaveBusy ? <Loader2 className="auto-spin" size={17} /> : <Check size={17} />}
              {chapterSaveBusy ? '正在保存' : '保存并进入审稿'}
            </button>
          </div>
          {chapterRevisionNotice && (
            <div className={chapterRevisionBusy ? 'chapter-revision-tip active' : 'chapter-revision-tip'} role="status" aria-live="polite">
              {chapterRevisionBusy ? <Loader2 size={15} /> : <Bot size={15} />}
              <span>{chapterRevisionNotice}</span>
              {!chapterRevisionBusy && (
                <button type="button" onClick={() => setChapterRevisionNotice('')} aria-label="关闭提示">
                  <X size={13} />
                </button>
              )}
            </div>
          )}

        </main>

        <aside className="wb-score">
          <div className="score-head">
            <span>当前评分</span>
            {activeChapterIsReviewing && (
              <span className="score-head-running" role="status" aria-live="polite">
                <Loader2 size={14} />
                AI评分中
              </span>
            )}
          </div>
          <div className="score-number">
            <strong>{activeChapterScore}</strong>
            <span>/ 100</span>
            <b className={activeChapterScore < 80 ? 'pending' : ''}>{activeChapterGrade}</b>
          </div>
          <div className="score-list">
            {activeScoreDimensions.map(({ label, score: itemScore, color }) => (
              <button key={label} type="button" onClick={() => setToast(`${label}评分 ${itemScore}`)}>
                <span>{label}</span>
                <i><b style={{ width: `${itemScore}%`, background: color }} /></i>
                <em>{itemScore}</em>
              </button>
            ))}
          </div>
          <section className="advice-box score-empty-box">
            <h3><Check size={15} />评分状态</h3>
            {!activeChapterHasContent ? (
              <>
                <strong>当前章节还没有正文</strong>
                <p>全自动生成只负责连续写作并保存正文，不会在队列中等待评分。</p>
                <p>正文生成后可按需要手动评分，不会再阻塞生成和保存。</p>
              </>
            ) : activeChapterIsReviewing ? (
              <>
                <strong>AI 正在评分</strong>
                <p>正在按一致性、节奏、爽点钩子、伏笔人设四项判定。</p>
                <p>评分完成后会自动刷新当前面板。</p>
                <span className="score-running-line"><i /></span>
              </>
            ) : activeChapterHasReview ? (
              <>
                <strong>本章评分已完成</strong>
                <p>评分仅用于展示当前章节的一致性、节奏、爽点和伏笔人设表现。</p>
              </>
            ) : (
              <>
                <strong>当前章节尚未评分</strong>
                <p>检测到正文已有内容，系统会在后台自动开始评分。</p>
                <p>评分结果会从后端重新同步到这里。</p>
              </>
            )}
          </section>
          <button className={activeChapterIsReviewing ? 'rescore scoring' : 'rescore'} type="button" disabled={revisionBusy || activeChapterIsReviewing || !activeChapter.backendId || !activeChapterHasContent} onClick={recalcScore}>
            <RefreshCw size={16} />
            <span>{activeChapterIsReviewing ? '正在评分' : '重新评分'}</span>
          </button>
        </aside>
      </div>
      ) : (
        <>
          <StagePanels
            activeTab={activeTab}
            projectId={project?.id}
            activeInspirationId={activeInspirationId}
            inspirationItems={workbenchInspirations}
            generationSettings={generationSettings}
            workbenchChapters={chapters}
            stageCompletion={stageCompletion}
            onInspirationTitleChange={setInspirationTitle}
            score={score}
            setToast={setToast}
            onOpenChapters={() => switchTab('chapters', true)}
            onOpenCharacters={() => switchTab('characters', true)}
            onOpenOutline={() => switchTab('outline', true)}
            onOpenSettings={() => switchTab('settings', true)}
            onOpenPublish={() => switchTab('publish', true)}
            onStageCompletionChange={handleStageCompletionChange}
            onWorkbenchRefresh={loadWorkbench}
            onInspirationSelect={selectInspirationOverview}
            onEnsureWorkbenchReady={ensureWorkbenchReady}
            onAssetsRefresh={loadWorkbenchAssets}
            onRecalcScore={recalcScore}
            onRequireLogin={requireLogin}
            onGenerationFailure={handleAiFailure}
          />
        </>
      )}

      <aside
        className={aiDrawerOpen ? 'ai-assistant-drawer open' : 'ai-assistant-drawer'}
        aria-hidden={!aiDrawerOpen}
        aria-label="AI助手"
        style={{
          left: aiPanelLayout.x,
          top: aiPanelLayout.y,
          width: aiPanelLayout.width,
          height: aiPanelLayout.height,
        }}
      >
          <div className="ai-drawer-head" onPointerDown={dragAiPanel}>
            <button className="ai-drawer-icon" type="button" aria-label="会话历史" onClick={() => setAiHistoryOpen((open) => !open)}>
              <History size={17} />
            </button>
            <button className="ai-drawer-icon" type="button" aria-label="添加新会话" onClick={requestNewAiSession}>
              <Plus size={18} />
            </button>
            <div className="ai-drawer-title">
              <strong>AI助手</strong>
            </div>
            <button className="ai-drawer-icon" type="button" aria-label="设置" onClick={() => setAssistantSettingsOpen((open) => !open)}>
              <Settings size={17} />
            </button>
            <button className="ai-drawer-close" type="button" aria-label="收起AI助手" onClick={() => setAiDrawerOpen(false)}>
              <X size={17} />
            </button>

            {aiHistoryOpen && (
              <div className="ai-popover ai-history-popover">
                {aiSessions.map((session) => (
                  <button className={session.id === activeAiSessionId ? 'ai-history-item active' : 'ai-history-item'} type="button" key={session.id} onClick={() => { setActiveAiSessionId(session.id); setAiHistoryOpen(false); }}>
                    <strong>{session.title}</strong>
                    <span>{session.last} · {session.messages.length || 0} 轮</span>
                  </button>
                ))}
              </div>
            )}

            {assistantSettingsOpen && (
              <div className="ai-popover ai-settings-popover">
                <strong>助手设置</strong>
                <label><input type="checkbox" defaultChecked /> 回答时优先保持当前阶段目标</label>
                <label><input type="checkbox" defaultChecked /> 自动带入项目上下文</label>
                <label><input type="checkbox" /> 生成前先列修改点</label>
              </div>
            )}
          </div>

          <div className="ai-chat-thread" ref={aiChatThreadRef}>
            <article className="ai-context-tip">
              <span>当前环节</span>
              <strong>{aiSceneConfig.tipTitle}</strong>
              <p>{aiSceneConfig.tipBody}</p>
            </article>
            <article className="ai-preset-panel">
              <strong>常用问题</strong>
              <div className="ai-task-list">
                {aiPresetPrompts.map((task) => <button type="button" key={task} onClick={() => fillPresetPrompt(task)}>{task}</button>)}
              </div>
            </article>
            {activeAiSession?.messages.length ? (
              <div className="ai-session-thread">
                {activeAiSession.messages.map((message, index) => (
                <article className={`ai-chat-card ${message.role}`} key={`${message.role}-${index}`}>
                  <div className="ai-chat-card-head">
                    <strong>{message.role === 'user' ? '你' : 'AI'}</strong>
                    {message.role === 'assistant' && message.text.trim() && (
                      <button
                        type="button"
                        aria-label="复制AI回复"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => copyAiMessage(message.text)}
                        title="复制这条回复"
                      >
                        <Copy size={13} />
                        复制回复
                      </button>
                    )}
                  </div>
                  {message.role === 'assistant' ? renderAssistantMarkdown(message.text) : <p>{message.text}</p>}
                </article>
                ))}
              </div>
            ) : null}
          </div>

          <form
            className="ai-chat-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void askWorkbenchAssistant();
            }}
          >
            <label className="assistant-input">
              <Bot size={16} />
              <input
                value={revisionPrompt}
                placeholder={aiSceneConfig.placeholder}
                onChange={(event) => setRevisionPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void askWorkbenchAssistant();
                  }
                }}
              />
            </label>
            <button className="ai-send-button" type="submit" disabled={!revisionPrompt.trim() || assistantBusy}>
              <Send size={17} />
              {assistantBusy ? '回答中' : '发送'}
            </button>
          </form>

          {['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw'].map((direction) => (
            <span
              aria-hidden="true"
              className={`ai-resize-handle ${direction}`}
              key={direction}
              onPointerDown={(event) => resizeAiPanel(event, direction)}
            />
          ))}
      </aside>

      {newAiSessionConfirmOpen && (
        <div className="confirm-backdrop ai-session-confirm-backdrop" role="presentation" onClick={() => setNewAiSessionConfirmOpen(false)}>
          <section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="new-ai-session-title" onClick={(event) => event.stopPropagation()}>
            <strong id="new-ai-session-title">新建 AI 会话？</strong>
            <p>当前对话会保留在左上角的会话历史中。新会话会清空输入框，方便开始新的问题。</p>
            <div>
              <button type="button" onClick={() => setNewAiSessionConfirmOpen(false)}>继续当前会话</button>
              <button className="danger" type="button" onClick={() => { setNewAiSessionConfirmOpen(false); startNewAiSession(); }}>新建会话</button>
            </div>
          </section>
        </div>
      )}

      {inkRechargePrompt && (
        <div className="ink-recharge-backdrop" role="presentation" onClick={() => setInkRechargePrompt(null)}>
          <section className="ink-recharge-modal" role="dialog" aria-modal="true" aria-labelledby="ink-recharge-title" onClick={(event) => event.stopPropagation()}>
            <button className="ink-recharge-close" type="button" aria-label="关闭充值提示" onClick={() => setInkRechargePrompt(null)}>
              <X size={17} />
            </button>
            <span className="ink-recharge-icon"><Coins size={22} /></span>
            <strong id="ink-recharge-title">墨水余额不足</strong>
            <p>{inkRechargePrompt}</p>
            <p className="ink-recharge-hint">充值后即可继续使用 AI 完成当前创作步骤。</p>
            <div className="ink-recharge-actions">
              <button type="button" onClick={() => setInkRechargePrompt(null)}>暂不充值</button>
              <button className="ink-recharge-primary" type="button" onClick={() => { setInkRechargePrompt(null); window.location.hash = '#payment/plus'; }}>
                前往充值
                <ArrowRight size={17} />
              </button>
            </div>
          </section>
        </div>
      )}

      {autoGenerateOpen && (
        <div className="auto-generate-backdrop" role="dialog" aria-modal="true" aria-label="全自动生成确认">
          <section className="auto-generate-modal">
            <button
              className="auto-modal-close"
              type="button"
              aria-label="关闭"
              onClick={() => setAutoGenerateOpen(false)}
            >
              <X size={17} />
            </button>

            <div className="auto-modal-head">
              <span><Zap size={19} /></span>
              <div>
                <strong>全自动生成</strong>
                <em>从当前章节继续写作，自动生成后续章节草稿</em>
              </div>
            </div>

            {autoGenerateState === 'checking' ? (
              <div className="auto-checking">
                <Loader2 size={32} />
                <strong>正在评估用户账户余额</strong>
                <p>正在核算剩余章节、单章预计消耗和账户可用额度。</p>
                <i><b /></i>
              </div>
            ) : (
              <div className="auto-result">
                <div className="auto-balance-card">
                  <Coins size={22} />
                  <span>当前余额</span>
                  <strong>{formatInkBalance(autoGenerateEstimate.accountBalance)}</strong>
                  <em>
                    单章预计 {autoGenerateEstimate.costPerChapter.toLocaleString()} · 预计生成 {autoGenerateEstimate.plannedChapters} 章
                  </em>
                </div>

                <div className="auto-result-copy">
                  <span>
                    {autoGenerateState === 'running'
                      ? '自动生成中'
                      : autoGenerateState === 'done'
                        ? '自动生成完成'
                        : autoGenerateState === 'stopped'
                          ? '已终止'
                          : '评估完成'}
                  </span>
                  <h3>
                    {autoGenerateState === 'running'
                      ? autoGenerateActiveStep
                        ? `正在生成第 ${autoGenerateActiveStep.index} 章`
                        : '正在按章节顺序生成草稿'
                      : autoGenerateState === 'done'
                        ? `已完成 ${autoGenerateDoneCount} 章自动生成`
                        : autoGenerateState === 'stopped'
                          ? '已停止后续章节生成'
                      : autoGenerateEstimate.plannedChapters > 0
                        ? `您的当前余额预计可写 ${autoGenerateEstimate.affordableChapters} 章`
                        : autoGenerateEstimate.remainingChapters > 0 ? '墨水余额不足，暂不能生成下一章' : '当前没有待生成章节'}
                  </h3>
                  <p>
                    {autoGenerateState === 'running'
                      ? '需要停止时可立即终止，系统会保留已经完成的章节，不再继续生成后续章节。'
                      : autoGenerateState === 'done'
                        ? '系统已按大纲、人物图谱、伏笔表和审核规则连续生成，并已保存每章草稿。窗口即将自动关闭。'
                        : autoGenerateState === 'stopped'
                          ? '本次全自动生成已经停止，您可以检查已生成章节，确认后再继续。'
                      : `当前还剩 ${autoGenerateEstimate.remainingChapters} 章待写，本次建议自动生成 ${autoGenerateEstimate.plannedChapters} 章。${autoGenerateEstimate.basis ? `估算依据：${autoGenerateEstimate.basis}。` : ''}`}
                  </p>
                </div>

                {autoGenerateSteps.length > 0 && (
                  <div className="auto-chapter-queue" aria-label="自动生成章节队列" aria-live="polite">
                    {autoGenerateSteps.map((item) => (
                      <div className={`auto-chapter-step ${item.status}`} key={item.id}>
                        <span>
                          {item.status === 'done' ? <Check size={14} /> : item.status === 'running' || item.status === 'reviewing' ? <Loader2 size={14} /> : item.status === 'failed' || item.status === 'stopped' ? <X size={14} /> : <Circle size={14} />}
                        </span>
                        <div>
                          <strong>{item.index} · {item.title}</strong>
                          <em>{item.message}</em>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="auto-generate-steps">
                  <span>锁定当前设定</span>
                  <span>逐章生成草稿</span>
                  <span>自动保存版本</span>
                </div>

                {autoGenerateState === 'running' ? (
                  <button className="auto-start-button auto-stop-button" type="button" onClick={stopAutoGenerate}>
                    <AlertTriangle size={18} />
                    立即终止
                  </button>
                ) : autoGenerateState === 'done' ? (
                  <button className="auto-start-button" type="button" onClick={() => setAutoGenerateOpen(false)}>
                    <Check size={18} />
                    知道了
                  </button>
                ) : autoGenerateState === 'stopped' ? (
                  <button className="auto-start-button" type="button" onClick={() => setAutoGenerateOpen(false)}>
                    <Check size={18} />
                    已停止，关闭
                  </button>
                ) : (
                  <button className="auto-start-button" type="button" disabled={autoGenerateEstimate.plannedChapters <= 0 || autoGenerateBlockedByOtherTask} onClick={startAutoGenerate}>
                    <Play size={18} />
                    {autoGenerateBlockedByOtherTask
                      ? '当前 AI 任务处理中'
                      : autoGenerateEstimate.plannedChapters > 0 ? '开始自动生成' : '余额不足，无法生成'}
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      <footer className="wb-statusbar">
        <span>UTF-8</span>
        <span>Markdown</span>
        <span>字数 <strong>{wordCount.toLocaleString()}{targetWordCount ? ` / ${targetWordCount.toLocaleString()}` : ''}</strong></span>
        <span>章节 <strong>{generatedChapterCount.toLocaleString()} / {plannedChapterCount.toLocaleString()}</strong></span>
        <em>自动保存正常</em>
      </footer>
    </div>
  );
};

export default WorkbenchPage;
